import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import * as mapboxgl from 'mapbox-gl';
import Map, { Source, Layer, Marker, Popup } from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import { calcAreaRequirement, siteZone } from '../utils/projectCalcs';
import { sitesApi, layersApi } from '../services/api';
import AnalysisPanel from './AnalysisPanel';
import MapStyleToggle from './MapStyleToggle';
import SiteFinder from './SiteFinder';
import { useOrg } from '../contexts/OrgContext';
import { tenantUseCase } from '../utils/tenantScope';
import LayerControls from './LayerControls';
// CoordinateSearchBox moved into SiteFinder — see SiteFinder.jsx
import { LAYER_GROUPS, FINLAND_LAYER_GROUPS, ITALY_LAYER_GROUPS, DATACENTER_LAYER_GROUPS } from '../config/layers.js';
import { useTranslation } from '../translations';
import { useAuth0 } from '@auth0/auth0-react';
import { useIsPremium } from '../contexts/PremiumContext';
import { useRegionLock } from '../contexts/RegionLockContext';
import { openCalendly } from '../utils/calendly';
import PremiumLock from './PremiumLock/PremiumLock';
import { openFeedback } from './FeedbackWidget';
import { readPlanCode, planCodeName, planCodeSuitability } from '../utils/planCodes';
import { SiteHoverCard, SubstationHoverCard, ParcelHoverCard, parcelVerdict, hoverPlacement } from './map/HoverCards';
import { rankOf } from '../utils/resultOrder';

// Hover goes to the most specific thing under the cursor: a point (a
// substation, a port) before the search result around it, and a result
// before the parcel under it. Mapbox lists features top layer first, and the
// parcel hit areas cover whole parcels, so features[0] alone would let a
// parcel hide every substation on it.
const HOVER_PRIORITY = [
  'grid-connections', 'ports-circle', 'ports-labels', 'co2-sources-circles', 'dh-plants-circles',
  'ultranet-sites-circles', 'ultranet-backbone-lines', 'search-teigs-fill',
  'nearby-parcels-good-hit', 'nearby-parcels-bad-hit', 'nearby-parcels-good-outline', 'nearby-parcels-bad-outline',
];
function pickHoverFeature(features) {
  if (!features || !features.length) return null;
  for (const id of HOVER_PRIORITY) {
    const f = features.find(x => x?.layer?.id === id);
    if (f) return f;
  }
  return features[0];
}
const CARD_LAYERS = new Set([
  'grid-connections', 'search-teigs-fill',
  'nearby-parcels-good-hit', 'nearby-parcels-bad-hit', 'nearby-parcels-good-outline', 'nearby-parcels-bad-outline',
]);
import '../styles/SiteScreener.css';
import '../styles/premiumLock.css';
import './PremiumLock/PremiumLock.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

// Norway center coordinates
const NORWAY_CENTER = {
  longitude: 10.7522,
  latitude: 63.4305,
  zoom: 5
};

// Italy center coordinates — Phase 1 skeleton (map default only, no
// data pipeline yet). See backend/app/services/country_config.py 'IT'.
const ITALY_CENTER = {
  longitude: 12.5,
  latitude: 41.9,
  zoom: 5.5
};

// Fallback framing if RegionLock context isn't ready when the
// auto-fly fires. Stavanger centre — matches FREE_TIER_BBOX in
// backend/app/core/tier_enforcement.py (lat 58.70-59.30, lon
// 5.30-6.20). When the lock IS resolved, the auto-fly uses the
// real bbox via fitBounds, so this constant only matters in the
// (rare) race window between Mapbox load and Premium status fetch.
const FREE_TIER_VIEW = {
  longitude: 5.75,    // (5.30 + 6.20) / 2
  latitude:  59.00,   // (58.70 + 59.30) / 2
  zoom: 8.5,
};

// ─── Props (SPEC_DEMO.md PR-4) ─────────────────────────────────────────
// Five optional knobs that default to current behavior. The point of
// PR-4 is that NO caller passes these yet — `<SiteScreener language=...
// />` in App.jsx is the only consumer today and review reduces to
// "no new prop is being threaded, so the authed map UX cannot have
// changed." PR-5 introduces DemoMap which actually consumes them.
//
//   layerWhitelist:        string[] | null
//                          If set, only layer ids in this list are
//                          included in interactiveLayerIds (click hit-
//                          test). Visual layer rendering is unchanged
//                          here — DemoMap pairs this with
//                          disableLayerControls so non-whitelisted
//                          layer toggles are simply not surfaced.
//   maxZoomBeforeBlur:     number | null
//                          If set, applies the CSS class
//                          `map-zoom-locked` to the map container when
//                          viewState.zoom > maxZoomBeforeBlur. The
//                          actual blur + "book a demo to zoom in"
//                          overlay live in demo CSS (PR-5). We
//                          deliberately do NOT call setMaxZoom on the
//                          Mapbox instance — that disables zoom
//                          entirely; the blur is a soft signal.
//   onSiteClick:           (event) => void | null
//                          If set, replaces the default handleMapClick.
//                          The demo caller does its own hit-testing
//                          against the demo's site set.
//   disableLayerControls:  bool
//                          If true, hides the LayerControls / LCOH /
//                          Draw / SiteFinder UI overlays.
//   hideExportButtons:     bool
//                          Currently a no-op inside this component
//                          (no export controls live here — they're in
//                          ProjectsPage.jsx). Declared as part of the
//                          PR-4 contract so DemoMap can pass it and
//                          future export UI added here is gated by
//                          default.
const SiteScreener = ({
  language = 'en',
  layerWhitelist = null,
  maxZoomBeforeBlur = null,
  onSiteClick = null,
  disableLayerControls = false,
  hideExportButtons = false,
  initialCountry = 'NO',
}) => {
  const t = useTranslation(language);
  const { isPremium, loading: premiumLoading } = useIsPremium();
  const regionLock = useRegionLock();
  const { user: auth0User } = useAuth0();
  // Per-session dismiss for the free-tier banner. sessionStorage so it
  // reappears next visit but doesn't nag on every map-state change.
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('seidr.freeTierBannerDismissed') === '1'; }
    catch { return false; }
  });
  const mapRef = useRef();
  // Free users get auto-flown to Helgeland on mount so the Run button
  // is immediately useful — without this they'd land on the Norway-
  // wide default and have to know to navigate north before screening.
  // The fly fires ONCE, after both Auth0+PremiumContext resolve and
  // the map has loaded. Premium users are never auto-flown.
  // NOTE: mapLoaded MUST be declared here (not later in the file)
  // because the auto-fly useEffect below reads it in its deps array —
  // putting it after that useEffect would cause a TDZ error
  // ("Cannot access 'mapLoaded' before initialization") at render.
  const autoFlownRef = useRef(false);
  // One-shot: first time the user picks a site we hide the zoning
  // overlay (along with the satellite auto-switch). The pink
  // arealformål fill is noise at parcel-zoom — the analyst wants
  // imagery, not the schematic. Set to true after we've done it
  // once so re-toggling zoning back on later sticks.
  const zoningAutoHiddenRef = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const loadTimeoutRef = useRef(null);
  const teigFetchAbortRef = useRef(null);
  const teigDebounceRef = useRef(null);
  const [viewState, setViewState] = useState(initialCountry === 'IT' ? ITALY_CENTER : NORWAY_CENTER);
  const [clickedPoint, setClickedPoint] = useState(null);


  // Deep link from the Projects map (#/?lat=..&lon=..&project=..). The
  // screener is kept mounted across routes (App.jsx, 2026-10-04), so a
  // mount-time read isn't enough — listen for hash changes and fly to
  // the pin the user clicked. Nothing else about the session changes.
  useEffect(() => {
    const flyToHash = () => {
      try {
        const h = window.location.hash || '';
        if (!h.startsWith('#/?')) return;
        const q = new URLSearchParams(h.slice(3));
        const lat = parseFloat(q.get('lat')); const lon = parseFloat(q.get('lon'));
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        const m = mapRef.current;
        if (m && typeof m.flyTo === 'function') m.flyTo({ center: [lon, lat], zoom: 14, duration: 1200 });
        setClickedPoint({ longitude: lon, latitude: lat });
      } catch { /* malformed hash — ignore */ }
    };
    flyToHash();
    window.addEventListener('hashchange', flyToHash);
    return () => window.removeEventListener('hashchange', flyToHash);
  }, []);
  const [analysisResult, setAnalysisResult] = useState(null);

  // ── Site → feature links (2026-10-05) ───────────────────────────────
  // `substation`: drawn by default for every analysed site (blue, dashed,
  // labelled). `focus`: the feature the user clicked in the Data tab
  // (amber). Both resolve through /api/v1/sites/locate so the endpoint
  // of the line is the real feature (closest point on a lake shoreline,
  // the substation's own coordinate), never a guess.
  const [siteLinks, setSiteLinks] = useState({ substation: null, port: null, focus: null });
  const locateFeature = useCallback(async (kind, name, fromLat, fromLon) => {
    const apiBase = import.meta.env.VITE_API_URL || '';
    const q = new URLSearchParams({ kind, lat: String(fromLat), lon: String(fromLon) });
    if (name && name !== '*') q.set('name', name);
    const r = await fetch(`${apiBase}/api/v1/sites/locate?${q.toString()}`);
    if (!r.ok) throw new Error(`locate ${kind} ${name || ''}: HTTP ${r.status}`);
    return r.json();
  }, []);
  const fitToLink = useCallback((fromLon, fromLat, toLon, toLat) => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const b = [[Math.min(fromLon, toLon), Math.min(fromLat, toLat)], [Math.max(fromLon, toLon), Math.max(fromLat, toLat)]];
    try { map.fitBounds(b, { padding: { top: 80, bottom: 80, left: 520, right: 420 }, duration: 1000, maxZoom: 15 }); } catch { /* map torn down */ }
  }, []);
  const handleLocate = useCallback(async (kind, name, label, coords = null) => {
    const from = analysisResult?.location;
    if (!from || from.latitude == null || from.longitude == null) return;
    try {
      // Explicit coordinates (e.g. an NVE hydropower plant the panel
      // already has lat/lon for) skip the locator round-trip.
      let hit;
      if (coords && coords.lat != null && coords.lon != null) {
        const dLat = (coords.lat - from.latitude) * 111.32;
        const dLon = (coords.lon - from.longitude) * 111.32 * Math.cos(from.latitude * Math.PI / 180);
        hit = { lat: coords.lat, lon: coords.lon, name: label || name, matched_by: 'given', distance_km: Math.hypot(dLat, dLon) };
      } else {
        hit = await locateFeature(kind, name, from.latitude, from.longitude);
      }
      const link = { kind, lon: hit.lon, lat: hit.lat,
                     label: `${label || hit.name}${hit.matched_by === 'nearest' ? ' (nearest match)' : ''} · ${hit.distance_km.toFixed(1)} km` };
      setSiteLinks(prev => ({ ...prev, focus: link }));
      fitToLink(from.longitude, from.latitude, hit.lon, hit.lat);
    } catch (e) {
      console.warn('[locate]', e);
    }
  }, [analysisResult, locateFeature, fitToLink]);
  // The precise buildable polygon for the currently-open analysis,
  // pushed up from AnalysisPanel after /buildable-detail returns.
  // Rendered as a green overlay on the map so the user can see the
  // shape underneath the headline number.
  const [buildablePolygon, setBuildablePolygon] = useState(null);
  const [projectMw, setProjectMw] = useState(20);
  const analysisResultRef = useRef(null);
  useEffect(() => { analysisResultRef.current = analysisResult; }, [analysisResult]);

  const [savedSites, setSavedSites] = useState([]);

  const [hoverInfo, setHoverInfo] = useState(null);
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/light-v11');
  const [showSiteFinder, setShowSiteFinder] = useState(true);
  const [mapBounds, setMapBounds] = useState(null);
  const [currentBbox, setCurrentBbox] = useState(null);
  const [isScreening, setIsScreening] = useState(false);
  const [scanKey, setScanKey] = useState(0);
  const scanTimerRef = useRef(null);

  // Polygon draw tool
  const drawRef = useRef(null);
  const [drawActive, setDrawActive] = useState(false);
  const [drawnArea, setDrawnArea] = useState(null); // { ha, m2, perimeterKm, centroid, loading, slope_pct, elevation_m, flood_1000yr, landslide, quick_clay }

  // Feature 3: active WMS layers from LayerControls registry
  const [activeWmsLayers, setActiveWmsLayers] = useState({});
  const handleWmsLayerChange = useCallback((newState) => {
    setActiveWmsLayers(newState);
  }, []);

  // PeeringDB facilities layer — lazily fetched the first time the
  // user toggles "Peering Facilities" on. Backend caches the upstream
  // call for 24h so this is one cheap GeoJSON download per session.
  const [peeringFacilitiesData, setPeeringFacilitiesData] = useState(null);
  useEffect(() => {
    if (!activeWmsLayers.peering_facilities) return;
    if (peeringFacilitiesData) return;
    const apiBase = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBase}/api/v1/layers/peering-facilities`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPeeringFacilitiesData(data); })
      .catch(() => {});
  }, [activeWmsLayers.peering_facilities, peeringFacilitiesData]);

  // Ultranet Italia partner carrier network — small dataset (~1,441 points,
  // 2 backbone route documents), fetch once per session when toggled on.
  const [ultranetSitesData, setUltranetSitesData] = useState(null);
  const [ultranetBackboneData, setUltranetBackboneData] = useState(null);
  useEffect(() => {
    if (!activeWmsLayers.ultranet_carrier_sites) return;
    if (ultranetSitesData) return;
    const apiBase = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBase}/api/v1/layers/ultranet-carrier-sites`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setUltranetSitesData(data); })
      .catch(() => {});
  }, [activeWmsLayers.ultranet_carrier_sites, ultranetSitesData]);
  useEffect(() => {
    if (!activeWmsLayers.ultranet_backbone) return;
    if (ultranetBackboneData) return;
    const apiBase = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBase}/api/v1/layers/ultranet-backbone`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setUltranetBackboneData(data); })
      .catch(() => {});
  }, [activeWmsLayers.ultranet_backbone, ultranetBackboneData]);

  // Country + use case are the tenant's (utils/tenantScope.js); SiteFinder
  // reports them once the branding has arrived.
  const { branding } = useOrg();
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [useCase, setUseCase] = useState(() => tenantUseCase(branding));

  // Stale-closure-safe access to country/use-case for callbacks that are
  // memoised with [] deps (onMapLoad's draw handlers, handleSearchTeigSelect,
  // etc.). Without this, switching country to FI after mount leaves those
  // closures pointing at the initial 'NO' value.
  const selectedCountryRef = useRef(selectedCountry);
  const useCaseRef = useRef(useCase);
  useEffect(() => { selectedCountryRef.current = selectedCountry; }, [selectedCountry]);
  useEffect(() => { useCaseRef.current = useCase; }, [useCase]);

  // (moved below the useCase declaration 2026-10-10: its dependency array
  // read useCase before initialisation and crashed the map on load.)
  // Default links whenever the analysed site changes: nearest substation
  // (blue) and, for hydrogen / ammonia, nearest port (green): a data-centre
  // site doesn't ship by sea. Both resolve through /locate so the
  // endpoint is the feature's real coordinate.
  useEffect(() => {
    const from = analysisResult?.location;
    const site = analysisResult?._site;
    setSiteLinks({ substation: null, port: null, focus: null });
    if (!from || !site) return;
    let cancelled = false;
    const subName = site.nearest_grid_name || site.nearest_grid?.name;
    const kv = site.nearest_grid_voltage_kv ?? site.nearest_grid?.voltage_level;
    const subLabel = kv != null ? `${subName} · ${kv} kV` : subName;
    const portName = site.nearest_port_name || site.port_name;
    const put = (key, link) => { if (!cancelled) setSiteLinks(prev => ({ ...prev, [key]: link })); };
    // Harbour-screen results: the default lines go to the grid point whose
    // published capacity passed and to the harbour the screen measured.
    const scr = site.harbour_screen;
    const sgp = scr?.grid_point;
    const shb = scr?.harbour;
    if (sgp && Number.isFinite(sgp.lat) && Number.isFinite(sgp.lon)) {
      put('substation', { kind: 'substation', lon: sgp.lon, lat: sgp.lat,
                          label: `${sgp.name} · ${sgp.available_mw} MW free · ${sgp.distance_km} km` });
    }
    if (shb && Number.isFinite(shb.lat) && Number.isFinite(shb.lon)) {
      put('port', { kind: 'port', lon: shb.lon, lat: shb.lat, label: `${shb.name} · ${shb.distance_km} km` });
    }
    if (subName && !(sgp && Number.isFinite(sgp.lat))) {
      if (site.nearest_grid_lat != null && site.nearest_grid_lon != null) {
        put('substation', { kind: 'substation', lon: site.nearest_grid_lon, lat: site.nearest_grid_lat, label: subLabel });
      } else {
        locateFeature('substation', subName, from.latitude, from.longitude)
          .then(hit => put('substation', { kind: 'substation', lon: hit.lon, lat: hit.lat, label: `${subLabel} · ${hit.distance_km.toFixed(1)} km` }))
          .catch(e => console.warn('[locate substation]', e));
      }
    }
    if (portName && useCase !== 'data_center' && !(shb && Number.isFinite(shb.lat))) {
      locateFeature('port', portName, from.latitude, from.longitude)
        .then(hit => put('port', { kind: 'port', lon: hit.lon, lat: hit.lat,
                                   label: `${hit.name}${hit.matched_by === 'nearest' ? ' (nearest port)' : ''} · ${hit.distance_km.toFixed(1)} km` }))
        .catch(e => console.warn('[locate port]', e));
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisResult?.location?.latitude, analysisResult?.location?.longitude, analysisResult?._site?.nearest_grid_name, analysisResult?._site?.nearest_port_name, useCase]);

  // Free-tier auto-fly to the locked region. Fires once when:
  //   • Auth0 + PremiumContext have resolved (premiumLoading=false)
  //   • The user is non-premium
  //   • The Mapbox instance has loaded (mapLoaded=true)
  // Uses fitBounds on the RegionLock bbox when available — that
  // way the camera lands exactly where the search will run.
  // Previously hardcoded FREE_TIER_VIEW (Vestlandet coords) was
  // outside the new Stavanger maxBounds clamp, so the rubber-band
  // would dump the user onto an edge of the bbox rather than the
  // centre. 2026-06-08.
  useEffect(() => {
    if (autoFlownRef.current) return;
    if (premiumLoading || isPremium || !mapLoaded) return;
    autoFlownRef.current = true;
    const map = mapRef.current?.getMap?.();
    if (map && regionLock.bbox) {
      const b = regionLock.bbox;
      map.fitBounds(
        [[b.min_lon, b.min_lat], [b.max_lon, b.max_lat]],
        { padding: 60, duration: 1200, essential: true },
      );
    } else {
      mapRef.current?.flyTo?.({
        center: [FREE_TIER_VIEW.longitude, FREE_TIER_VIEW.latitude],
        zoom:   FREE_TIER_VIEW.zoom,
        duration: 1200,
        essential: true,
      });
    }
  }, [premiumLoading, isPremium, mapLoaded, regionLock.bbox]);

  // Feature 1C: terrain slope grid overlay
  const [slopeGrid, setSlopeGrid] = useState(null); // array of {lon, lat, slope_pct}
  const [slopeThreshold, setSlopeThreshold] = useState(15);
  const [slopeLoading, setSlopeLoading] = useState(false);
  const [suggestedPolygon, setSuggestedPolygon] = useState(null); // GeoJSON geometry from terrain-clip

  // LCOH heatmap
  const [lcohHeatmapVisible, setLcohHeatmapVisible] = useState(false);
  const [searchResultSites, setSearchResultSites] = useState([]);

  // Teig search results from /search SSE (fully enriched teiger from SiteFinder)
  const [searchTeigs, setSearchTeigs] = useState([]);
  // "#n" on the map = the same site's place in the results list
  const searchRank = useMemo(() => rankOf(searchTeigs), [searchTeigs]);

  // Parcel selection
  const [parcelSelectMode, setParcelSelectMode] = useState(false);
  const parcelSelectModeRef = useRef(false); // ref for stale-closure-safe access in event handlers
  const setParcelMode = (val) => {
    const next = typeof val === 'function' ? val(parcelSelectModeRef.current) : val;
    parcelSelectModeRef.current = next;
    setParcelSelectMode(next);
  };
  const [selectedParcels, setSelectedParcels] = useState([]);
  const [neededAreaHa, setNeededAreaHa] = useState(null);
  const totalSelectedHa = selectedParcels.reduce((s, p) => s + p.area_ha, 0);

  // Nearby parcels — auto-loaded when a substation is selected
  const [zoneGeometries, setZoneGeometries] = useState({}); // teig_id -> DIBK zone GeoJSON geometry
  const [nearbyParcels, setNearbyParcels] = useState(null);
  const [nearbyParcelsLoading, setNearbyParcelsLoading] = useState(false);
  const [nearbyParcelsEnriching, setNearbyParcelsEnriching] = useState(false);
  const [nearbyParcelCount, setNearbyParcelCount] = useState(null);
  const [selectedTeig, setSelectedTeig] = useState(null); // the one clicked teig
  const [teigZone, setTeigZone] = useState(null);         // arealplaner zone at teig centroid
  const [teigZoneLoading, setTeigZoneLoading] = useState(false);
  const [teigListOpen, setTeigListOpen] = useState(false);
  const [showZoomPrompt, setShowZoomPrompt] = useState(false);

  // Phase 2 enrichment tracking (enrich-site called on teig click)
  const [teigEnriching, setTeigEnriching] = useState(false);
  const enrichedTeigIdsRef = useRef(new Set()); // sync check (no re-render needed)
  const [enrichedTeigIds, setEnrichedTeigIds] = useState(new Set()); // for map halo layer
  const projectMwUserSetRef = useRef(false); // true once user/Finn soner sets MW
  const [defaultMwNote, setDefaultMwNote] = useState(false);
  // teigCriteria: all teig-level criteria received from SiteFinder via onCriteriaChange.
  // These control which teiger display as green (pass) vs. red (fail) on the map.
  // They are NOT used to filter substations — that is Phase 1 (min_voltage only).
  // All distance/terrain fields below are measured from the teig centroid.
  const [teigCriteria, setTeigCriteria] = useState({
    // Substation-level (also used by Phase 1 SQL)
    minVoltage: 66,
    // Teig-level (Phase 2 green/red classification only)
    maxGridDistance: 5.0,      // teig centroid → nearest substation (km)
    minAreaHa: 4.6,            // default: 20 MW PEM
    minProtectedDistance: 1.0, // teig centroid → nearest protected area (km)
    maxPortDistance: 20.0,     // teig centroid → nearest port (km)
    minPortDepth: 6.0,         // minimum navigable depth at nearest port (m)
    maxWaterDistance: 15.0,    // teig centroid → nearest lake or river (km)
    maxSlopePct: 20,           // max slope at teig (%)
    maxElevationM: 800,        // max elevation at teig (m above sea level)
  });

  // Compute zone intersections: teig ∩ DIBK zone polygon = actual buildable area
  const zoneIntersections = React.useMemo(() => {
    const empty = { type: 'FeatureCollection', features: [] };
    if (!nearbyParcels?.features || Object.keys(zoneGeometries).length === 0) return { good: empty, bad: empty };
    const good = [], bad = [];
    for (const f of nearbyParcels.features) {
      if (f === selectedTeig) continue;
      const id = f.properties?.teigid || f.properties?.matrikkelnummer || f.id;
      const zoneGeom = zoneGeometries[id];
      if (!zoneGeom) continue;
      try {
        const intersection = turf.intersect(f, turf.feature(zoneGeom));
        if (!intersection) continue;
        const p = f.properties || {};
        const suit = parseFloat(p.zone_suitability || 0);
        const isGood = suit >= 0.6 && p.har_kulturminne !== 'true' && p.tvist !== 'true'
          && p.har_forurensning !== 'true' && p.flood_zone !== true && p.landslide_zone !== true;
        (isGood ? good : bad).push({ ...intersection, properties: { suit } });
      } catch {}
    }
    return {
      good: { type: 'FeatureCollection', features: good },
      bad:  { type: 'FeatureCollection', features: bad },
    };
  }, [nearbyParcels, zoneGeometries, selectedTeig]);

  // LCOH heatmap GeoJSON — derived from search results
  const lcohGeoJSON = useMemo(() => ({
    type: 'FeatureCollection',
    features: (searchResultSites || [])
      .filter(s => s.lcoh_usd_per_kg != null && s.location)
      .map(s => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.location.longitude, s.location.latitude] },
        properties: {
          lcoh: s.lcoh_usd_per_kg,
          name: s.nearest_grid?.name || s.id || '',
          zone: s.lcoh_elspot_zone || '',
        },
      })),
  }), [searchResultSites]);

  // Zone intersection for the selected teig highlight
  const selectedZoneIntersection = React.useMemo(() => {
    if (!selectedTeig) return null;
    const id = selectedTeig.properties?.teigid || selectedTeig.properties?.matrikkelnummer || selectedTeig.id;
    const zoneGeom = zoneGeometries[id];
    if (!zoneGeom) return null;
    try { return turf.intersect(selectedTeig, turf.feature(zoneGeom)); } catch { return null; }
  }, [selectedTeig, zoneGeometries]);

  // Layer visibility — critical layers on, secondary off by default
  const [layers, setLayers] = useState({
    gridConnections:          { visible: true,  data: null },
    environmentalConstraints: { visible: true,  data: null },
    ports:                    { visible: true,  data: null },
    zoning:                   { visible: true,  wms: true },
    parcels:                  { visible: true,  wms: true },
    co2Sources:               { visible: true,  data: null },
  });

  const handleToggleLayer = useCallback((layerKey) => {
    setLayers(prev => ({
      ...prev,
      [layerKey]: { ...prev[layerKey], visible: !prev[layerKey].visible },
    }));
  }, []);

  // Country switch — clear NO-only data layers immediately so they stop
  // rendering on the FI/IT map. Re-fetch happens automatically via the
  // viewport-change debounce, but until that completes the previously
  // loaded NO data (ports, environmental constraints, CO2 sources)
  // would otherwise persist on screen for several seconds. Substations
  // re-load with FI/IT data via gridConnections (backend UNIONs NO+FI+IT
  // tables in /api/v1/layers/grid-connections, viewport bbox naturally
  // scopes results to the right country).
  useEffect(() => {
    if (selectedCountry === 'FI' || selectedCountry === 'IT') {
      setLayers(prev => ({
        ...prev,
        ports: { ...prev.ports, data: null },
        environmentalConstraints: { ...prev.environmentalConstraints, data: null },
        co2Sources: { ...prev.co2Sources, data: null },
      }));
    }
  }, [selectedCountry]);

  const handleSearchStart = useCallback(() => {
    setScanKey(k => k + 1);
    setIsScreening(true);
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    scanTimerRef.current = setTimeout(() => setIsScreening(false), 5000);

    // Free-tier UX: every search snaps the map back to the locked region
    // so the user always sees results in the region they're searching.
    // Without this, a free user who panned away + clicked Run would watch
    // the search complete with results "off-screen" and think it broken.
    // Premium users keep whatever view they had.
    if (!isPremium) {
      const map = mapRef.current?.getMap?.();
      if (map && regionLock.bbox) {
        const b = regionLock.bbox;
        map.fitBounds(
          [[b.min_lon, b.min_lat], [b.max_lon, b.max_lat]],
          { padding: 60, duration: 1100, essential: true },
        );
      } else {
        mapRef.current?.flyTo?.({
          center: [FREE_TIER_VIEW.longitude, FREE_TIER_VIEW.latitude],
          zoom:   FREE_TIER_VIEW.zoom,
          duration: 1100,
          essential: true,
        });
      }
    }
  }, [isPremium, regionLock.bbox]);

  useEffect(() => {
    return () => { if (scanTimerRef.current) clearTimeout(scanTimerRef.current); };
  }, []);

  const loadSavedSites = async () => {
    try {
      const response = await sitesApi.list();
      setSavedSites(response.data);
    } catch (error) {
      // Silently handle authentication errors - user not logged in
      if (error.response?.status === 403 || error.response?.status === 401) {
        console.log('User not authenticated - skipping saved sites');
        setSavedSites([]);
      } else {
        console.error('Error loading sites:', error);
      }
    }
  };

  // Helper function to get current map bounds
  const getMapBounds = useCallback(() => {
    if (!mapRef.current) return null;
    const map = mapRef.current.getMap();
    const bounds = map.getBounds();
    const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
    return bbox;
  }, []);

  // Load layer data when bounds change
  const onMapLoad = useCallback(() => {
    loadLayerData();
    // mapLoaded triggers the free-tier auto-fly to Stavanger (see the
    // useEffect near the top of this component).
    setMapLoaded(true);

    // Initialise MapboxDraw — no native controls (we provide our own button)
    const map = mapRef.current.getMap();

    // Region lock — when the user is non-premium and the URL pins
    // them to a region (Stavanger by default), prevent panning /
    // zooming outside the region bbox. setMaxBounds is a hard limit:
    // attempts to pan past the edge rubber-band back to the bbox.
    // Premium users skip this — they get the full Norway map.
    if (regionLock.locked && regionLock.bbox) {
      const b = regionLock.bbox;
      // Mapbox expects [[swLon, swLat], [neLon, neLat]].
      map.setMaxBounds([[b.min_lon, b.min_lat], [b.max_lon, b.max_lat]]);
      // Snap into the region on load so the existing auto-fly doesn't
      // immediately violate the bounds.
      map.fitBounds([[b.min_lon, b.min_lat], [b.max_lon, b.max_lat]],
        { padding: 40, duration: 0 });
    }
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      defaultMode: 'simple_select',
    });
    map.addControl(draw, 'top-left');
    drawRef.current = draw;

    const updateArea = async () => {
      const data = draw.getAll();
      if (!data.features.length) { setDrawnArea(null); setDrawActive(false); return; }
      const areaM2 = turf.area(data);
      const perimeterKm = data.features.reduce((sum, f) => {
        try { return sum + turf.length(f, { units: 'kilometers' }); } catch { return sum; }
      }, 0);
      // Compute centroid of union of all drawn features
      const union = data.features.length === 1
        ? data.features[0]
        : turf.union(...data.features);
      const centroid = turf.centroid(union);
      const [lon, lat] = centroid.geometry.coordinates;

      setDrawActive(false); // exit draw mode after completing polygon
      setDrawnArea({
        ha: Math.round(areaM2 / 10000 * 10) / 10,
        m2: Math.round(areaM2),
        perimeterKm: Math.round(perimeterKm * 10) / 10,
        centroid: { lat, lon },
        loading: true,
      });

      // Enrich with slope + hazard data from backend (country-aware).
      // Read country from ref because onMapLoad has [] deps and would
      // otherwise capture the initial 'NO' value forever.
      try {
        const { data: analysis } = await sitesApi.analyzeArea(lat, lon, selectedCountryRef.current);
        setDrawnArea(prev => prev ? { ...prev, loading: false, ...analysis } : null);
      } catch {
        setDrawnArea(prev => prev ? { ...prev, loading: false } : null);
      }
    };

    map.on('draw.create', updateArea);
    map.on('draw.update', updateArea);
    map.on('draw.delete', () => { setDrawnArea(null); setDrawActive(false); });

    // Map↔Projects navigation fix (audit 2026-05-28). App.jsx keeps the
    // map mounted under `display: none` while the Projects view is up.
    // When the user navigates back to Map, the container reflows from
    // 0×0 to its real size, but mapbox-gl doesn't know — its canvas
    // stays at the dimensions it was last rendered at, so the user
    // sees only the top-left ~1/3 of the map with black everywhere
    // else. Watch the container with ResizeObserver and trigger
    // map.resize() on every dimension change; double-rAF lets the
    // browser finish the layout before we read the new size.
    const container = map.getContainer();
    if (container && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          try { map.resize(); } catch { /* map may already be destroyed */ }
        }));
      });
      ro.observe(container);
      // Stash for cleanup on unmount — onMapLoad runs once so we tag
      // the ref instead of returning a cleanup.
      mapRef.current._resizeObserver = ro;
    }
  }, []);

  const loadLayerData = async (bbox = null) => {
    try {
      // Get current viewport bbox if not provided
      const currentViewportBbox = bbox || getMapBounds();

      // Get current zoom level to determine appropriate limits
      const map = mapRef.current?.getMap();
      const zoom = map ? map.getZoom() : 5;

      // Zoom-based port limit - fewer ports at low zoom levels
      const getPortLimit = (z) => {
        if (z < 6) return 0;      // No ports at country level
        if (z < 8) return 50;     // 50 largest ports at region level
        if (z < 10) return 200;   // 200 ports at sub-region level
        return 500;               // 500 ports at detail level (reduced from 10,000)
      };

      const portLimit = getPortLimit(zoom);

      // Load grid connections, environmental constraints, and ports.
      // Ports + environmental constraints are NO-only data sources
      // (Kystdatahuset + Miljødirektoratet); fetching them when the user
      // has selected FI/IT just renders Norwegian points on the wrong
      // map. Skip both for FI/IT to keep the country selection honest.
      const isFi = selectedCountryRef.current === 'FI';
      const isIt = selectedCountryRef.current === 'IT';
      const loadPromises = [
        layersApi.getGridConnections(currentViewportBbox),
        (isFi || isIt) ? Promise.resolve({ data: { type: 'FeatureCollection', features: [] } })
             : layersApi.getPorts(currentViewportBbox, null, null, portLimit),
      ];

      // Only load environmental constraints at higher zoom levels (NO only)
      if (zoom >= 10 && !isFi && !isIt) {
        loadPromises.push(layersApi.getEnvironmentalConstraints(currentViewportBbox));
      }

      const results = await Promise.all(loadPromises);
      const [gridData, portsData, envData] = results;

      // Also load DH plants for FI at zoom >= 8
      let dhData = null;
      if (selectedCountryRef.current === 'FI' && zoom >= 8 && currentViewportBbox) {
        try {
          const apiBase = import.meta.env.VITE_API_URL || '';
          const r = await fetch(`${apiBase}/api/v1/layers/dh-plants-fi?bbox=${currentViewportBbox}`);
          if (r.ok) dhData = await r.json();
        } catch {}
      }

      // Debug logging removed for production
      if (import.meta.env.DEV) console.log('Loaded layer data:', {
        zoom: zoom.toFixed(1),
        gridConnections: gridData.data?.features?.length || 0,
        ports: portsData.data?.features?.length || 0,
        portLimit,
        environmentalConstraints: envData ? (envData.data?.features?.length || 0) : 'not loaded (zoom < 10)',
        dhPlants: dhData?.features?.length || 0,
      });

      setLayers(prev => ({
        ...prev,
        gridConnections: { ...prev.gridConnections, data: gridData.data },
        ports: { ...prev.ports, data: portsData.data },
        dhPlants: dhData ? { visible: true, data: dhData } : (prev.dhPlants || { visible: false, data: null }),
        // Only update environmental constraints if loaded
        environmentalConstraints: envData
          ? { ...prev.environmentalConstraints, data: envData.data }
          : prev.environmentalConstraints
      }));
    } catch (error) {
      console.error('Error loading layer data:', error);
    }
  };

  // Debounced function to reload data when viewport changes
  const debouncedLoadLayerData = useCallback(() => {
    // Clear existing timeout
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }

    // Set new timeout - wait 500ms after user stops moving
    loadTimeoutRef.current = setTimeout(() => {
      const newBbox = getMapBounds();
      if (newBbox && newBbox !== currentBbox) {
        if (import.meta.env.DEV) console.log('Viewport changed, reloading data for bbox:', newBbox);
        setCurrentBbox(newBbox);
        loadLayerData(newBbox);
      }
    }, 500);
  }, [currentBbox, getMapBounds]);

  // Load individual layer data on demand
  const loadLayer = async (layerName) => {
    // Skip WMS layers - they don't need data loading
    if (layers[layerName]?.wms) return;
    if (layers[layerName]?.data) return; // Already loaded

    try {
      const bbox = getMapBounds();
      let data;
      switch (layerName) {
        case 'environmentalConstraints':
          data = await layersApi.getEnvironmentalConstraints(bbox);
          break;
        case 'ports':
          data = await layersApi.getPorts(bbox, null, null, 10000);
          break;
        default:
          return;
      }

      setLayers(prev => ({
        ...prev,
        [layerName]: { ...prev[layerName], data: data.data }
      }));
    } catch (error) {
      console.error(`Error loading ${layerName}:`, error);
    }
  };

  const fetchParcelAtPoint = async ({ lng, lat }) => {
    const zoom = mapRef.current?.getMap?.()?.getZoom?.() ?? mapRef.current?.getZoom?.() ?? 0;
    if (zoom < 12) return;
    const apiBase = import.meta.env.VITE_API_URL || '';
    try {
      const resp = await fetch(`${apiBase}/api/v1/layers/parcel-at-point?lng=${lng}&lat=${lat}`);
      const geojson = await resp.json();
      const feature = geojson.features?.[0];
      if (!feature) return;
      selectNearbyTeig(feature);
    } catch (err) {
      console.error('Parcel WFS error:', err);
    }
  };

  // Coordinate-search entry point exposed to SiteFinder's sidebar input.
  // Backend's parcel-at-point falls back to nearest-within-2km when no
  // teig contains the point exactly, so this should always return a
  // result for coords inside Norway. Returns { ok, reason, info } so
  // SiteFinder can render either a success note ("Nearest parcel,
  // 47 m from your point") or an error string.
  //
  // Plain function rather than useCallback because selectNearbyTeig is
  // declared further down the component body — useCallback would
  // access it via its deps array during render, hitting the temporal
  // dead zone.
  const lookupParcelAtCoordinates = async (lat, lng) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return { ok: false, reason: 'Invalid coordinates' };
    }
    // Region lock — refuse coord lookups outside the locked bbox so
    // a free user can't bypass the map clamp by typing coordinates.
    // Backend re-clamps anyway, but surfacing the boundary here gives
    // a clearer message than "no parcel within 2 km".
    if (regionLock.locked && regionLock.bbox) {
      const b = regionLock.bbox;
      const inside = lat >= b.min_lat && lat <= b.max_lat
                  && lng >= b.min_lon && lng <= b.max_lon;
      if (!inside) {
        return {
          ok: false,
          reason: `Outside available area — ${regionLock.label || 'the locked region'} only`,
        };
      }
    }
    const apiBase = import.meta.env.VITE_API_URL || '';
    try {
      // Fire the WFS request first; once we have the feature we re-fly
      // to its centroid so the camera lands on the actual selected
      // parcel (not just the user's typed point — which can sit in a
      // road, fjord, or cadastral gap and end up off-screen from the
      // parcel that was actually picked).
      const resp = await fetch(
        `${apiBase}/api/v1/layers/parcel-at-point?lng=${lng}&lat=${lat}`
      );
      if (!resp.ok) {
        return { ok: false, reason: `Lookup failed (${resp.status})` };
      }
      const geojson = await resp.json();
      const feature = geojson.features?.[0];
      if (!feature) {
        return { ok: false, reason: 'No parcel within 2 km of those coordinates' };
      }
      // Pan to the parcel's centroid so it ends up centred on screen.
      // Falls back to the user's typed coords if centroid math fails.
      let center = [lng, lat];
      try {
        const c = turf.centroid(feature);
        if (c?.geometry?.coordinates) center = c.geometry.coordinates;
      } catch {}
      mapRef.current?.flyTo?.({ center, zoom: 16, duration: 1200, essential: true });
      // Auto-switch to satellite (audit 2026-05-29) — same rationale as
      // handleSearchTeigSelect: at parcel-zoom level satellite is more
      // informative than the schematic style.
      setMapStyle('mapbox://styles/mapbox/satellite-streets-v12');
      if (!zoningAutoHiddenRef.current) {
        setLayers(prev => prev.zoning?.visible
          ? { ...prev, zoning: { ...prev.zoning, visible: false } }
          : prev);
        zoningAutoHiddenRef.current = true;
      }
      selectNearbyTeig(feature);
      const match = feature.properties?.lookup_match;
      const dist = feature.properties?.lookup_distance_m;
      const info = match === 'nearest' && dist
        ? `Selected nearest parcel — ${dist} m from your point`
        : null;
      return { ok: true, info };
    } catch (err) {
      console.error('Coordinate lookup failed:', err);
      return { ok: false, reason: 'Lookup failed — check coordinates' };
    }
  };

  const handleMapClick = (event) => {
    // Always check loaded teiger first (viewport parcels)
    if (nearbyParcels?.features?.length) {
      const pt = turf.point([event.lngLat.lng, event.lngLat.lat]);
      const hit = nearbyParcels.features.find(f => {
        try { return turf.booleanPointInPolygon(pt, f); } catch { return false; }
      });
      if (hit) {
        selectNearbyTeig(hit);
        return;
      }
    }
    // Check search result assemblages (from Zone-first or Smart search)
    if (searchTeigs?.length) {
      const pt = turf.point([event.lngLat.lng, event.lngLat.lat]);
      const hit = searchTeigs.find(t => {
        if (!t.geometry) return false;
        try {
          return turf.booleanPointInPolygon(pt, { type: 'Feature', geometry: t.geometry });
        } catch { return false; }
      });
      if (hit) {
        handleSearchTeigSelect(hit, projectMw);
        return;
      }
    }
    // Always try WFS lookup at zoom >= 12 (no mode required)
    const zoom = mapRef.current?.getMap?.()?.getZoom?.() ?? mapRef.current?.getZoom?.() ?? 0;
    if (zoom >= 12) {
      fetchParcelAtPoint(event.lngLat);
    }
  };

  const handleSaveSite = async () => {
    if (!clickedPoint || !analysisResult) return;

    const siteName = prompt('Enter a name for this site:');
    if (!siteName) return;

    try {
      await sitesApi.create({
        name: siteName,
        location: clickedPoint,
        description: analysisResult.summary,
      });

      alert('Site saved successfully!');
      loadSavedSites();
    } catch (error) {
      console.error('Error saving site:', error);
      alert('Failed to save site');
    }
  };

  const getSuitabilityColor = (score) => {
    if (score >= 0.8) return '#22c55e'; // Green
    if (score >= 0.6) return '#eab308'; // Yellow
    if (score >= 0.4) return '#f97316'; // Orange
    return '#ef4444'; // Red
  };

  // Official plan-code names (utils/planCodes, shared with the backend).
  const getArealformalName = (code) => planCodeName(code) || `Formål ${code}`;

  // Query WMS GetFeatureInfo for zoning layers
  const queryZoningInfo = useCallback(async (lngLat, point) => {
    if (!layers.zoning?.visible || !mapRef.current) return null;

    const map = mapRef.current.getMap();
    const bounds = map.getBounds();
    const size = map.getContainer().getBoundingClientRect();

    // WMS 1.3.0 with EPSG:4326 uses axis order Y,X (lat,lon) for BBOX
    const bbox = `${bounds.getSouth()},${bounds.getWest()},${bounds.getNorth()},${bounds.getEast()}`;

    // Determine which layer to query based on zoom level
    const zoom = map.getZoom();
    const useRegulering = zoom >= 11;
    const wmsUrl = useRegulering
      ? 'https://nap.ft.dibk.no/services/wms/reguleringsplaner/'
      : 'https://nap.ft.dibk.no/services/wms/kommuneplaner/';
    const wmsLayers = useRegulering ? 'arealformal_vn1' : 'arealformal_kp';

    const params = new URLSearchParams({
      SERVICE: 'WMS', VERSION: '1.3.0', REQUEST: 'GetFeatureInfo',
      LAYERS: wmsLayers, QUERY_LAYERS: wmsLayers,
      CRS: 'EPSG:4326', BBOX: bbox,
      WIDTH: Math.round(size.width), HEIGHT: Math.round(size.height),
      I: Math.round(point.x), J: Math.round(point.y),
      INFO_FORMAT: 'application/json'
    });

    try {
      const response = await fetch(`${wmsUrl}?${params}`);
      const json = await response.json();
      const props = json.features?.[0]?.properties;
      if (!props) return null;
      const reading = readPlanCode(props, useRegulering);
      const code = reading?.code;
      const planId = props['arealplanId.planidentifikasjon'];
      const municipality = props['arealplanId.kommunenummer'];
      if (code) {
        return {
          type: 'zoning',
          code,
          name: reading.name,
          planId,
          municipality,
          layerType: useRegulering ? 'Reguleringsplan' : 'Kommuneplan'
        };
      }
    } catch (error) {
      console.error('Error querying WMS:', error);
    }

    return null;
  }, [layers.zoning]);

  const onHover = useCallback(async (event) => {
    // Disable all hover tooltips while drawing — they block the cursor
    // and make it impossible to see where the polygon is being drawn.
    if (drawActive) {
      setHoverInfo(null);
      return;
    }
    const {
      features,
      point: { x, y },
      lngLat
    } = event;

    const hoveredFeature = pickHoverFeature(features);

    if (hoveredFeature) {
      setHoverInfo({
        feature: hoveredFeature,
        x,
        y,
        w: event.target?.getContainer?.()?.clientWidth || null,
      });
    } else {
      // Zoning hover query disabled - was firing on every mouse move
      // causing excessive DIBK API calls. Users can click for zoning details.
      // The WMS layer already shows zoning colors visually.
      setHoverInfo(null);
    }
  }, [drawActive]);

  const onMouseLeave = useCallback(() => {
    setHoverInfo(null);
  }, []);

  const loadParcelsForCurrentView = useCallback(async () => {
    return; // replaced by viewport fetch below
  }, []);

  const fetchTeigForViewport = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const zoom = map.getZoom();
    if (zoom < 12) {
      // Too zoomed out — clear teiger and show hint
      setNearbyParcels(null);
      setNearbyParcelCount(null);
      return;
    }

    // Debounce: wait 1.5s after map stops moving
    if (teigDebounceRef.current) clearTimeout(teigDebounceRef.current);
    teigDebounceRef.current = setTimeout(async () => {
      const bounds = map.getBounds();
      const min_lon = bounds.getWest();
      const min_lat = bounds.getSouth();
      const max_lon = bounds.getEast();
      const max_lat = bounds.getNorth();

      // Abort any in-flight fetch
      if (teigFetchAbortRef.current) teigFetchAbortRef.current.abort();
      const controller = new AbortController();
      teigFetchAbortRef.current = controller;

      setNearbyParcelsLoading(true);
      setNearbyParcels(null);
      setNearbyParcelCount(null);

      const apiBase = import.meta.env.VITE_API_URL || '';
      const country = selectedCountryRef.current;
      try {
        const r = await fetch(
          `${apiBase}/api/v1/layers/parcels-in-bounds?min_lon=${min_lon}&min_lat=${min_lat}&max_lon=${max_lon}&max_lat=${max_lat}&country=${country}`,
          { signal: controller.signal }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        const features = data?.features || [];
        console.log('[viewport teiger]', features.length, '| matched:', data?.numberMatched);
        setNearbyParcels(features.length > 0 ? data : null);
        setNearbyParcelCount(features.length);

        if (features.length > 0) {
          setNearbyParcelsEnriching(true);
          const BATCH = 200;
          const allTeiger = features.map(f => {
            const ring = f.geometry?.coordinates?.[0] || [];
            if (!ring.length) return null;
            const clng = ring.reduce((s, c) => s + c[0], 0) / ring.length;
            const clat = ring.reduce((s, c) => s + c[1], 0) / ring.length;
            const areal_ha = f.properties?.areal_ha != null ? parseFloat(f.properties.areal_ha) : null;
            return { id: f.properties?.teigid || f.properties?.matrikkelnummer || f.id, coords: [[clng, clat]], areal_ha };
          }).filter(Boolean);

          const batches = [];
          for (let i = 0; i < allTeiger.length; i += BATCH) batches.push(allTeiger.slice(i, i + BATCH));

          // Phase 1: zone + PostGIS distances (fast, all teiger).
          // For FI, the enrich-teig-zones endpoint queries grid_connections_fi
          // and the suitability grid instead of the NO-only tables.
          // Country is passed in the request body.
          Promise.all(
            batches.map(batch =>
              fetch(`${apiBase}/api/v1/layers/enrich-teig-zones`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ teiger: batch, country: selectedCountryRef.current }),
                signal: controller.signal,
              }).then(r => r.json()).then(d => d.zones || {})
            )
          )
            .then(results => {
              const zones = Object.assign({}, ...results);
              // Extract zone geometries into separate state (can't store GeoJSON objects in Mapbox feature properties)
              const newGeoms = {};
              for (const [id, z] of Object.entries(zones)) {
                if (z?.zone_geometry) { newGeoms[id] = z.zone_geometry; delete z.zone_geometry; }
              }
              setZoneGeometries(prev => ({ ...prev, ...newGeoms }));
              // Patch Phase 1 data into features
              setNearbyParcels(prev => {
                if (!prev?.features) return prev;
                return {
                  ...prev,
                  features: prev.features.map(f => {
                    const id = f.properties?.teigid || f.properties?.matrikkelnummer || f.id;
                    const z = zones[id];
                    return z ? { ...f, properties: { ...f.properties, ...z } } : f;
                  }),
                };
              });

              // Phase 2: NVE hazard + slope + port depth — only for zone-suitable candidates
              const candidates = allTeiger.filter(t => {
                const z = zones[t.id];
                return z && z.zone_suitability >= 0.6;
              }).map(t => {
                const z = zones[t.id];
                return z?.nearest_port_lon != null && z?.nearest_port_lat != null
                  ? { ...t, port_coords: [z.nearest_port_lon, z.nearest_port_lat] }
                  : t;
              });
              if (!candidates.length) return;

              fetch(`${apiBase}/api/v1/layers/enrich-teig-hazards`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ teiger: candidates, country: selectedCountryRef.current }),
                signal: controller.signal,
              })
                .then(r => r.json())
                .then(d => {
                  const hazards = d.hazards || {};
                  setNearbyParcels(prev => {
                    if (!prev?.features) return prev;
                    return {
                      ...prev,
                      features: prev.features.map(f => {
                        const id = f.properties?.teigid || f.properties?.matrikkelnummer || f.id;
                        const h = hazards[id];
                        return h ? { ...f, properties: { ...f.properties, ...h } } : f;
                      }),
                    };
                  });
                })
                .catch(() => {});
            })
            .catch(() => {})
            .finally(() => setNearbyParcelsEnriching(false));
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.warn('[viewport teiger] fetch failed:', e);
      } finally {
        setNearbyParcelsLoading(false);
      }
    }, 1500);
  }, []);

  const handleMapMove = useCallback((evt) => {
    setViewState(evt.viewState);

    // Update map bounds for site finder
    const map = mapRef.current?.getMap();
    if (map) {
      const bounds = map.getBounds();
      setMapBounds({
        min_lat: bounds.getSouth(),
        max_lat: bounds.getNorth(),
        min_lon: bounds.getWest(),
        max_lon: bounds.getEast()
      });

      // Reload all layers for new viewport (debounced)
      debouncedLoadLayerData();
    }

    // Fetch teiger for new viewport (zoom 12+, debounced)
    fetchTeigForViewport();
  }, [debouncedLoadLayerData, fetchTeigForViewport]);

  // Query arealplaner zone type at a lng/lat point using WMS GetFeatureInfo
  // Uses a tiny synthetic bbox so no screen coordinates are needed
  const queryZoningAtCoords = async (lng, lat) => {
    const delta = 0.0005; // ~50m
    // WMS 1.3.0 EPSG:4326 BBOX is lat,lon order
    const bbox = `${lat - delta},${lng - delta},${lat + delta},${lng + delta}`;
    const base = { SERVICE: 'WMS', VERSION: '1.3.0', REQUEST: 'GetFeatureInfo',
                   CRS: 'EPSG:4326', BBOX: bbox, WIDTH: 101, HEIGHT: 101, I: 50, J: 50,
                   INFO_FORMAT: 'application/json' };
    const sources = [
      // All three reguleringsplan stages — visual tiles render vn1+vn2+vn3; must query all
      { url: 'https://nap.ft.dibk.no/services/wms/reguleringsplaner/', layers: 'arealformal_vn1,arealformal_vn2,arealformal_vn3', type: 'Reguleringsplan' },
      { url: 'https://nap.ft.dibk.no/services/wms/kommuneplaner/', layers: 'arealformal_kp,arealformal_kdp', type: 'Kommuneplan' },
    ];
    for (const src of sources) {
      try {
        const params = new URLSearchParams({ ...base, LAYERS: src.layers, QUERY_LAYERS: src.layers });
        const json = await (await fetch(`${src.url}?${params}`)).json();
        const props = json.features?.[0]?.properties;
        const reading = readPlanCode(props, src.type === 'Reguleringsplan');
        const planId = props?.['arealplanId.planidentifikasjon'] ?? props?.['arealplanId'] ?? props?.['planidentifikasjon'];
        if (reading?.code) return { code: reading.code, name: reading.name, planId, layerType: src.type };
      } catch { /* silent */ }
    }
    return null;
  };

  // Patch analysisResult with real zone score from a teig click or WMS fallback.
  // Works regardless of whether analysis came from SSE list-click (_site path)
  // or substation select (parameters path).
  const applyZoneToAnalysis = useCallback((zone) => {
    if (!zone?.code) return;
    const zScore = planCodeSuitability(zone.code) ?? 0.25;
    const zoneLabel = `${zone.code} - ${zone.name}`;
    const zoneDetails = `${zone.name} (${zone.code}) — ${zone.layerType || 'Arealplan'}`;
    const zoneRating = zScore >= 0.9 ? 'Excellent' : zScore >= 0.7 ? 'Good' : zScore >= 0.4 ? 'Moderate' : 'Poor';
    setAnalysisResult(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      // Patch parameters path (substation-select result structure)
      if (prev.parameters) {
        const gS = (prev.parameters.grid_distance?.score ?? 3) / 5;
        const vS = (prev.parameters.grid_strength?.score ?? 3) / 5;
        const pS = (prev.parameters.protected_area_proximity?.score ?? 3) / 5;
        next.suitability_score = (zScore + gS + vS + pS) / 4;
        next.parameters = {
          ...prev.parameters,
          zoning: {
            ...prev.parameters.zoning,
            value: zoneLabel,
            score: Math.round(zScore * 5),
            rating: zoneRating,
            details: zoneDetails,
          },
        };
      }
      // Patch _site path (SSE list-click and map-click result structures)
      if (prev._site) {
        next._site = {
          ...prev._site,
          zoning_info: { ...(prev._site.zoning_info || {}), zone_code: zone.code, zone_name: zone.name },
          _zone_result: {
            ...(prev._site._zone_result || {}),
            zone_code: zone.code,
            zone_name: zone.name,
            zone_suitability: zScore,
          },
        };
      }
      return next;
    });
  }, []);

  const selectNearbyTeig = useCallback(async (feature) => {
    setSelectedTeig(feature);
    setTeigZone(null);
    setTeigZoneLoading(false);

    const props = feature.properties || {};
    // DIAG TEST 2 — map-click enrichment fields
    console.log('[DIAG T2] map-click props:', { zone_code: props.zone_code, zone_name: props.zone_name, grid_distance_km: props.nearest_grid_distance_km, port_name: props.nearest_port_name, water_dist_km: props.nearest_water_distance_km });
    const id = props.teigid || props.matrikkelnummer || 'teig';
    const area_ha = turf.area(feature) / 10000;
    setSelectedParcels([{ id, area_ha, geometry: feature.geometry }]);

    let centroid;
    try { centroid = turf.centroid(feature); } catch { return; }
    const [lng, lat] = centroid.geometry.coordinates;
    setClickedPoint({ longitude: lng, latitude: lat });

    // Derive country from the parcel's own coordinates, not the UI
    // country toggle. Users often coord-search into FI while the search
    // sidebar is still set to NO — the parcel came back correctly (backend
    // /parcel-at-point auto-detects) but any downstream enrichment call
    // keyed on the toggle would hit the wrong country. Mirrors
    // backend/app/services/country_config.py:detect_country_from_coordinates.
    const parcelCountry =
      (lat >= 59.5 && lat <= 70.5 && lng >= 20.0 && lng <= 31.5) ? 'FI' : 'NO';

    const current = analysisResultRef.current;

    // Build _location_assessment from enriched teig props (available immediately)
    // assess-location is still called for water_dist_km (lakes/rivers), which enrichment doesn't provide
    const teigLoc = {
      protected_dist_km: props.nearest_protected_distance_km ?? null,
      port_name:         props.nearest_port_name ?? null,
      port_dist_km:      props.nearest_port_distance_km ?? null,
      port_depth_m:          props.nearest_port_depth_m ?? null,
      port_depth_estimated:  props.nearest_port_depth_estimated ?? false,
      water_dist_km:     null, // fetched async below
      flood_zone:        props.flood_zone ?? null,
      landslide_zone:    props.landslide_zone ?? null,
      slope_pct:         props.slope_pct ?? null,
    };

    if (current?._site?._zone_result) {
      // Patch the existing zone analysis with this teig's data
      setAnalysisResult(prev => {
        if (!prev?._site?._zone_result) return prev;
        return {
          ...prev,
          _site: {
            ...prev._site,
            _zone_result: {
              ...prev._site._zone_result,
              zone_name: props.zone_name || prev._site._zone_result.zone_name,
              zone_code: props.zone_code || prev._site._zone_result.zone_code,
              zone_suitability: props.zone_suitability != null
                ? parseFloat(props.zone_suitability)
                : prev._site._zone_result.zone_suitability,
              area_ha,
              municipality_name: props.kommune || prev._site._zone_result.municipality_name,
              location: { latitude: lat, longitude: lng },
            },
            _location_assessment: {
              ...(prev._site._location_assessment || {}),
              ...teigLoc,
            },
          },
        };
      });
    } else {
      // No existing analysis — build one fresh from this teig
      const apiBase = import.meta.env.VITE_API_URL || '';
      // Only fetch nearest-grid if not already in enriched props.
      // Country from parcelCountry (parcel coords), not the UI toggle.
      const gridPromise = props.nearest_grid_name
        ? Promise.resolve({ name: props.nearest_grid_name, voltage_level: props.nearest_grid_voltage_kv, distance_km: props.nearest_grid_distance_km })
        : fetch(`${apiBase}/api/v1/sites/nearest-grid?lat=${lat}&lng=${lng}&country=${parcelCountry}`)
            .then(r => r.ok ? r.json() : null).catch(() => null);

      const zs = props.zone_suitability != null ? parseFloat(props.zone_suitability) : 0;
      const gridData = await gridPromise;

      setAnalysisResult({
        suitability_score: Math.max(zs, 0.01),
        location: { latitude: lat, longitude: lng },
        summary: `Teig ${props.matrikkelnummer || props.teigid || ''}`,
        _site: {
          _zone_result: {
            zone_name: props.zone_name || props.zone_code || 'Unknown zone',
            zone_code: props.zone_code || '?',
            zone_suitability: zs,
            zone_code_confidence: props.zone_code_confidence || 'unknown',
            area_ha,
            municipality: props.kommunenr || '',
            municipality_name: props.kommune || props.kommunenr || '',
            location: { latitude: lat, longitude: lng },
            nearest_grid: gridData
              ? { id: gridData.id, name: gridData.name, voltage_level: gridData.voltage_level, operator: gridData.operator, distance_km: gridData.distance_km }
              : { name: '–', voltage_level: 0, distance_km: 0 },
          },
          _search_criteria: {
            maxGridDistance: teigCriteria.maxGridDistance,
            minVoltage: teigCriteria.minVoltage,
            minAreaHa: teigCriteria.minAreaHa,
            minProtectedDistance: teigCriteria.minProtectedDistance,
            maxPortDistance: 20,
            minPortDepth: 6,
            maxWaterDistance: 15,
          },
          _location_assessment: teigLoc,
        },
      });
    }

    // Fetch async fields not available from teig enrichment and patch.
    // NO: teig props already carry protected/port/flood/landslide/slope from PostGIS
    //     enrichment; only water_dist_km is missing → patched below.
    // FI: MML palsta features carry none of those; /assess-location fills them
    //     via SYKE/MML/NLS services. Guard `cur.X == null` prevents overwriting
    //     NO's pre-enriched values.
    // Country derived from parcelCountry above (parcel coords), not the UI toggle.
    const apiBase = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBase}/api/v1/sites/assess-location?lat=${lat}&lng=${lng}&country=${parcelCountry}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setAnalysisResult(prev => {
          if (!prev?._site) return prev;
          const cur = prev._site._location_assessment || {};
          const patched = { ...cur };
          const fillIfNull = (k) => {
            if (cur[k] == null && data[k] != null) patched[k] = data[k];
          };
          fillIfNull('protected_dist_km');
          fillIfNull('port_name');
          fillIfNull('port_dist_km');
          fillIfNull('port_depth_m');
          fillIfNull('water_dist_km');
          fillIfNull('flood_zone');
          fillIfNull('landslide_zone');
          fillIfNull('slope_pct');
          return {
            ...prev,
            _site: {
              ...prev._site,
              _location_assessment: patched,
            },
          };
        });
      })
      .catch(() => {});

    // Zone enrichment (WMS fallback if not pre-enriched)
    if (props.zone_code) {
      const zone = { code: props.zone_code, name: props.zone_name || props.zone_code,
                     planId: props.zone_plan_id, layerType: props.zone_layer };
      setTeigZone(zone);
      applyZoneToAnalysis(zone);
    } else {
      setTeigZoneLoading(true);
      queryZoningAtCoords(lng, lat)
        .then(zone => { setTeigZone(zone); applyZoneToAnalysis(zone); })
        .catch(() => setTeigZone(null))
        .finally(() => setTeigZoneLoading(false));
    }

    // Phase 2 enrichment — NVDB roads, NVE waterways, species, solar, climate
    // Runs on every teig click (with or without Finn soner), skipped if already cached.
    // If user hasn't set project MW yet, default to 100 MW and show a note.
    if (!projectMwUserSetRef.current) {
      setProjectMw(100);
      setDefaultMwNote(true);
    }
    const teigId = props.teigid || props.matrikkelnummer || id;
    if (!enrichedTeigIdsRef.current.has(teigId)) {
      setTeigEnriching(true);
      // FI: /enrich-site is a NO-only stub. The FI equivalent is
      // /analyze-area — it runs SYKE hazards, NLS terrain, _enrich_fi_services
      // (grid/DH/climate/land cover) AND compute_powered_land_readiness, so
      // the response carries readiness_score + readiness_themes, which the
      // analysis panel body renders on. Same call handleSearchTeigSelect uses,
      // but the search path was only cherry-picking slope/elevation from the
      // response — here we merge the whole payload into _site.
      // Country derived from parcel coords (parcelCountry) so a coord-search
      // into FI with the UI toggle still on NO still enriches via the FI path.
      if (parcelCountry === 'FI') {
        sitesApi.analyzeArea(lat, lng, 'FI')
          .then(({ data }) => {
            if (!data) return;
            enrichedTeigIdsRef.current.add(teigId);
            setEnrichedTeigIds(prev => new Set([...prev, teigId]));
            setAnalysisResult(prev => {
              if (!prev?._site) return prev;
              return {
                ...prev,
                _site: {
                  ...prev._site,
                  ...data,
                  _location_assessment: {
                    ...(prev._site._location_assessment || {}),
                    ...(data._location_assessment || {}),
                  },
                },
              };
            });
          })
          .catch(err => console.warn('analyze-area (FI teig direct) failed:', err))
          .finally(() => setTeigEnriching(false));
      } else {
        fetch(`${apiBase}/api/v1/sites/enrich-site?lat=${lat}&lng=${lng}&country=${parcelCountry}`)
          .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
          .then(enriched => {
            enrichedTeigIdsRef.current.add(teigId);
            setEnrichedTeigIds(prev => new Set([...prev, teigId]));
            setAnalysisResult(prev => {
              if (!prev) return prev;
              return { ...prev, _site: { ...prev._site, ...enriched } };
            });
          })
          .catch(err => console.warn('enrich-site (teig direct) failed:', err))
          .finally(() => setTeigEnriching(false));
      }
    }
  }, [applyZoneToAnalysis, queryZoningAtCoords]); // eslint-disable-line react-hooks/exhaustive-deps


  // handleSubstationPan: pan/zoom to a substation from the results list WITHOUT opening
  // the analysis panel. The analysis panel only opens when the user clicks a specific teig.
  const handleSubstationPan = useCallback((site) => {
    if (!site?.location) return;
    mapRef.current?.flyTo({
      center: [site.location.longitude, site.location.latitude],
      zoom: 14,
      duration: 2000
    });
    setNearbyParcels(null);
    setZoneGeometries({});
    setNearbyParcelCount(null);
    setNearbyParcelsEnriching(false);
    setSelectedParcels([]);
    setSelectedTeig(null);
    setTeigZone(null);
    setTeigZoneLoading(false);
    setAnalysisResult(null);
    setTeigEnriching(false);
    setEnrichedTeigIds(new Set());
    enrichedTeigIdsRef.current = new Set();
    setDefaultMwNote(false);
    // Show zoom prompt briefly if already at low zoom
    const currentZoom = mapRef.current?.getMap?.()?.getZoom?.() ?? 14;
    if (currentZoom < 12) {
      setShowZoomPrompt(true);
      setTimeout(() => setShowZoomPrompt(false), 4000);
    }
    setTimeout(() => fetchTeigForViewport(), 2200);
  }, [fetchTeigForViewport]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSiteSelect = useCallback(async (site, projectParams = {}) => {
    // Fly to site location
    mapRef.current?.flyTo({
      center: [site.location.longitude, site.location.latitude],
      zoom: 14,
      duration: 2000
    });

    // Clear previous parcels + selection
    setNearbyParcels(null);
    setZoneGeometries({});
    setNearbyParcelCount(null);
    setNearbyParcelsEnriching(false);
    setSelectedParcels([]);
    setSelectedTeig(null);
    setTeigZone(null);
    setTeigZoneLoading(false);
    setTeigEnriching(false);
    setEnrichedTeigIds(new Set());
    enrichedTeigIdsRef.current = new Set();
    setDefaultMwNote(false);

    // Set needed area from project params; propagate country + use-case to map context
    const { powerMw = 20, technology: tech = 'PEM', country: newCountry = 'NO', useCase: newUseCase = 'hydrogen',
            waterAccessRequired = false, maxWaterDistance = null } = projectParams;
    if (newCountry !== selectedCountry) setSelectedCountry(newCountry);
    if (newUseCase !== useCase) setUseCase(newUseCase);
    const areaReq = calcAreaRequirement(powerMw, tech);
    setNeededAreaHa(areaReq.totalHa);
    setProjectMw(powerMw);
    projectMwUserSetRef.current = true;

    // Fetch teiger for viewport after flyTo animation settles (~2s)
    setTimeout(() => fetchTeigForViewport(), 2200);

    // Convert site finder result to analysis format
    setClickedPoint({
      longitude: site.location.longitude,
      latitude: site.location.latitude
    });

    // Build analysis result directly from site data - no API call needed!
    const gridScore = site.grid_distance_km <= 1 ? 5 :
                     site.grid_distance_km <= 3 ? 4 :
                     site.grid_distance_km <= 5 ? 3 :
                     site.grid_distance_km <= 8 ? 2 : 1;

    const voltageScore = site.voltage_level_kv >= 300 ? 5 :  // Gold
                        site.voltage_level_kv >= 132 ? 4 :  // Silver
                        site.voltage_level_kv >= 66 ? 3 :   // Bronze
                        site.voltage_level_kv >= 22 ? 2 : 1;

    const protectedScore = site.protected_distance_km >= 5 ? 5 :
                          site.protected_distance_km >= 2 ? 4 :
                          site.protected_distance_km >= 1 ? 3 :
                          site.protected_distance_km >= 0.5 ? 2 : 0;

    const suitabilityScore = (site.zoning_score + (gridScore/5) + (voltageScore/5) + (protectedScore/5)) / 4;

    const analysis = {
      location: site.location,
      suitability_score: suitabilityScore,
      summary: `${site.zoning_score === 1.0 ? 'Excellent' : site.zoning_score >= 0.8 ? 'Good' : 'Moderate'} site near ${site.nearest_grid?.name || 'grid connection'} (${site.voltage_level_kv}kV, ${site.grid_distance_km}km). Protected area ${site.protected_distance_km}km away. ${site.nearest_port ? `Nearest port: ${site.nearest_port} (${site.port_distance_km}km).` : ''}`,
      parameters: {
        grid_distance: {
          name: "Grid Distance",
          score: gridScore,
          value: `${site.grid_distance_km} km`,
          rating: gridScore >= 4 ? 'Excellent' : gridScore >= 3 ? 'Good' : gridScore >= 2 ? 'Moderate' : 'Poor',
          details: `${gridScore >= 4 ? 'Excellent' : gridScore >= 3 ? 'Good' : gridScore >= 2 ? 'Moderate' : 'Poor'} grid proximity: ${site.grid_distance_km}km to ${site.nearest_grid.name} (${site.nearest_grid.voltage_level || 'N/A'}kV)`,
          is_showstopper: false
        },
        protected_area_proximity: {
          name: "Protected Area Proximity",
          score: protectedScore,
          value: `${site.protected_distance_km} km`,
          rating: protectedScore >= 4 ? 'Excellent' : protectedScore >= 3 ? 'Good' : protectedScore >= 2 ? 'Moderate' : 'Critical',
          details: `${site.protected_distance_km >= 1 ? 'Safe' : 'CLOSE'} distance to protected areas: ${site.protected_distance_km}km`,
          is_showstopper: site.has_protected_overlap || site.protected_distance_km < 0.5
        },
        grid_strength: {
          name: "Grid Strength",
          score: voltageScore,
          value: `${site.voltage_level_kv >= 300 ? 'GOLD' : site.voltage_level_kv >= 132 ? 'SILVER' : site.voltage_level_kv >= 66 ? 'BRONZE' : 'WEAK'} - ${site.voltage_level_kv}kV`,
          rating: voltageScore >= 4 ? 'Excellent' : voltageScore >= 3 ? 'Good' : voltageScore >= 2 ? 'Moderate' : 'Poor',
          details: `${site.voltage_level_kv}kV grid connection at ${site.nearest_grid.name} (${site.grid_distance_km}km away)${site.nearest_grid.short_description ? ' - ' + site.nearest_grid.short_description : ''}`,
          is_showstopper: false
        },
        zoning: {
          name: "Zoning",
          score: Math.round(site.zoning_score * 5),
          value: site.zoning_info?.zone_code ? `${site.zoning_info.zone_code} - ${site.zoning_info.zone_name}` : 'Checking…',
          rating: site.zoning_score >= 0.9 ? 'Excellent' : site.zoning_score >= 0.7 ? 'Good' : 'Moderate',
          details: site.zoning_info?.zone_code ? `${site.total_parcel_area_ha?.toFixed(2) || 'N/A'} hectares of ${site.zoning_info.zone_name} (${site.zoning_info.zone_code}) in ${site.zoning_info.municipality}. ${site.zoning_info.plan_type || 'Zoned land'}.` : 'Zone data loading from WMS…',
          is_showstopper: false
        },
        port_distance: {
          name: "Port Distance",
          score: site.port_distance_km ? (site.port_distance_km <= 5 ? 5 : site.port_distance_km <= 10 ? 4 : site.port_distance_km <= 20 ? 3 : 2) : 0,
          value: site.port_distance_km ? `${site.port_distance_km} km` : 'N/A',
          rating: site.port_distance_km <= 5 ? 'Excellent' : site.port_distance_km <= 10 ? 'Good' : site.port_distance_km <= 20 ? 'Moderate' : 'Poor',
          details: site.nearest_port ? `Port: ${site.nearest_port} (${site.port_type || 'N/A'}) - ${site.port_distance_km}km away` : 'No port data available',
          is_showstopper: false
        },
        water_access: (() => {
          // water_distance_km is not returned by find-sites API; derive from lake/river
          const wdist = site.water_distance_km ??
            Math.min(site.lake_distance_km ?? Infinity, site.river_distance_km ?? Infinity);
          const wd = isFinite(wdist) ? wdist : null;
          return {
          name: "Water Access",
          score: wd ? (wd <= 1 ? 5 : wd <= 3 ? 4 : wd <= 5 ? 3 : wd <= 10 ? 2 : 1) : 0,
          value: wd ? `${wd.toFixed(1)} km` : 'N/A',
          rating: !wd ? 'Unknown' : wd <= 1 ? 'Excellent' : wd <= 3 ? 'Strong' : wd <= 5 ? 'Good' : wd <= 10 ? 'Moderate' : 'Poor',
          details: (() => {
            const parts = [];
            if (site.lake_name && site.lake_distance_km) {
              parts.push(`Lake: ${site.lake_name} (${site.lake_area_ha?.toFixed(1) || '?'} ha) - ${site.lake_distance_km}km`);
            }
            if (site.river_distance_km) {
              const rname = site.river_name || 'Elv (ukjent navn)';
              parts.push(`Elv: ${rname} (Order ${site.river_order || '?'}) - ${site.river_distance_km}km`);
            }
            return parts.length > 0 ? parts.join(' | ') : 'No water source data available';
          })(),
          is_showstopper: false
        }; })(),
      },
      _site: site,  // raw site object for full data display
      grid_connections: [site.nearest_grid],
      environmental_constraints: [], // Would need separate API call
      nearby_infrastructure: [],
      energy_resources: [],
      nearest_ports: site.nearest_port ? [{
        name: site.nearest_port,
        distance_km: site.port_distance_km,
        port_type: site.port_type || 'Unknown',
        system_name: null,
        municipality: null,
        is_active: null,
        owner: null,
        depth_meters: site.port_depth_m || null,
        vessel_capacity: site.port_vessel_capacity || null,
        depth_source: null
      }] : []
    };

    analysis.nearest_co2_source = computeNearestCo2(site.location.longitude, site.location.latitude);
    // Stash the user's water-access filter so AnalysisPanel can pass it
    // to /cooling-water — the endpoint now uses this threshold instead
    // of hardcoded 1.4 km / 2.0 km caps. Null when "water not required".
    analysis._userMaxWaterKm = waterAccessRequired && Number.isFinite(Number(maxWaterDistance))
      ? Number(maxWaterDistance) : null;
    setAnalysisResult(analysis);

    // For zone-first sites: fetch proximity data (protected/port/water) and patch criteria.
    // Read country from ref — handleSiteSelect has [] deps so the closure captures
    // the initial 'NO' value otherwise.
    if (site._zone_result) {
      const apiBase = import.meta.env.VITE_API_URL || '';
      const gridId = site._zone_result?.nearest_grid?.id;
      const qs = new URLSearchParams({
        lat: site.location.latitude,
        lng: site.location.longitude,
        country: selectedCountryRef.current,
        ...(gridId ? { grid_id: gridId } : {}),
      });
      fetch(`${apiBase}/api/v1/sites/assess-location?${qs}`)
        .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
        .then(loc => {
          setAnalysisResult(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              nearest_co2_source: loc.nearest_co2_source || prev.nearest_co2_source,
              _site: {
                ...prev._site,
                _location_assessment: loc,
              },
            };
          });
        })
        .catch(err => console.warn('assess-location failed:', err));
    }

    // Fetch enhanced criteria (species, solar, climate, roads, water rights) for this site
    // Runs regardless of whether advanced filters were used in the search
    {
      const apiBase = import.meta.env.VITE_API_URL || '';
      const enrichLat = site._zone_result ? site.location.latitude : site.location?.latitude;
      const enrichLng = site._zone_result ? site.location.longitude : site.location?.longitude;
      if (enrichLat && enrichLng && selectedCountryRef.current !== 'FI') {
        // FI: enrich-site backend returns {} for FI (NO-only services).
        // Skip the round-trip entirely. (Country read from ref because
        // handleSiteSelect has [] deps.)
        fetch(`${apiBase}/api/v1/sites/enrich-site?lat=${enrichLat}&lng=${enrichLng}&country=${selectedCountryRef.current}`)
          .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
          .then(enriched => {
            setAnalysisResult(prev => {
              if (!prev) return prev;
              return { ...prev, _site: { ...prev._site, ...enriched } };
            });
            // Score fields are returned by enrich-site (geo_service calls score_site(phase="full") inline).
            // Merge onto the site in the results list and re-sort.
            if (enriched?.composite_score != null || enriched?.score_phase === 'full') {
              setSearchResultSites(prev => {
                const updated = prev.map(s =>
                  (s === site || s?.nearest_grid?.id === site?.nearest_grid?.id)
                    ? {
                        ...s,
                        economics_score: enriched.economics_score ?? s.economics_score,
                        permitting_score: enriched.permitting_score ?? s.permitting_score,
                        construction_score: enriched.construction_score ?? s.construction_score,
                        composite_score: enriched.composite_score ?? s.composite_score,
                        score_drivers: enriched.score_drivers ?? s.score_drivers,
                        _score_phase: 'full',
                      }
                    : s
                );
                return [...updated].sort((a, b) => {
                  const aFull = a._score_phase === 'full';
                  const bFull = b._score_phase === 'full';
                  if (aFull && !bFull) return -1;
                  if (!aFull && bFull) return 1;
                  const aScore = aFull ? (a.composite_score || 0) : (a.economics_score || 0);
                  const bScore = bFull ? (b.composite_score || 0) : (b.economics_score || 0);
                  return bScore - aScore;
                });
              });
            }
          })
          .catch(err => console.warn('enrich-site failed:', err));
      }
    }

    // Async: query real zone data at site coords and patch suitability score
    queryZoningAtCoords(site.location.longitude, site.location.latitude)
      .then(zone => {
        if (!zone?.code) return;
        // Same reading and score as the backend (utils/planCodes)
        let realZoneScore = planCodeSuitability(zone.code) ?? 0.25;
        setAnalysisResult(prev => {
          if (!prev) return prev;
          const newSuitability = (realZoneScore + (gridScore / 5) + (voltageScore / 5) + (protectedScore / 5)) / 4;
          return {
            ...prev,
            suitability_score: newSuitability,
            parameters: {
              ...prev.parameters,
              zoning: {
                ...prev.parameters.zoning,
                value: `${zone.code} - ${zone.name}`,
                score: Math.round(realZoneScore * 5),
                rating: realZoneScore >= 0.9 ? 'Excellent' : realZoneScore >= 0.7 ? 'Good' : realZoneScore >= 0.4 ? 'Moderate' : 'Poor',
                details: `${zone.name} (${zone.code}) — ${zone.layerType}`,
              },
            },
          };
        });
      })
      .catch(() => {}); // best-effort, keep original score if fails
  }, [loadParcelsForCurrentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // handleSearchTeigSelect: called when user clicks a teig card from the SSE /search results.
  // Builds an analysis result from the pre-enriched teig object — no API call needed.
  const handleSearchTeigSelect = useCallback((teig, searchMw) => {
    if (!teig?.lat || !teig?.lon) return;
    mapRef.current?.flyTo({ center: [teig.lon, teig.lat], zoom: 15, duration: 1500 });
    // Auto-switch to satellite on site select (audit 2026-05-29). At this
    // zoom + intent (looking at one specific parcel), satellite is more
    // useful than the schematic light style — analyst wants to see what's
    // actually there (buildings, vegetation, roads). User can toggle
    // back via MapStyleToggle if they prefer the schematic view.
    setMapStyle('mapbox://styles/mapbox/satellite-streets-v12');
    if (!zoningAutoHiddenRef.current) {
      setLayers(prev => prev.zoning?.visible
        ? { ...prev, zoning: { ...prev.zoning, visible: false } }
        : prev);
      zoningAutoHiddenRef.current = true;
    }
    // Sync MW shown in analysis panel to the MW used in the search
    if (searchMw && Number.isFinite(searchMw)) {
      setProjectMw(searchMw);
      projectMwUserSetRef.current = true;
    }

    // Build _site-compatible object from teig enrichment data.
    // Field names must match what AnalysisPanel.jsx reads from analysis._site.
    const gridName = teig.nearest_grid_name || teig.src_sub_name;
    const gridVoltage = teig.nearest_grid_voltage_kv || teig.src_sub_kv;
    const gridPriceZone = siteZone(teig);   // the municipality's zone, not the substation's guess
    // CRITICAL: spread `teig` FIRST so all SOP fields the SSE search
    // produced (readiness_score, nearest_ixp_*, design_temp_*, snow_load,
    // dual_feed_available, second_grid_*, nearest_residential_*, noise_flag,
    // water_capacity_class, fi_*_data_available, river_name, lake_name,
    // and the readiness_themes object) are carried through to AnalysisPanel.
    // The explicit field assignments below then NORMALIZE specific NO/legacy
    // fields into the shapes the panel expects without dropping anything.
    // Bug pre-fix: this object listed only legacy fields explicitly, so the
    // entire SOP §10 / §10.4 / §12 / §14 dataset was silently stripped from
    // the panel even though it sat right on the SSE result.
    const siteObj = {
      ...teig,
      nearest_grid: {
        id:            teig.nearest_grid_id,
        name:          gridName,
        voltage_level: gridVoltage,
        price_zone:    gridPriceZone,
        distance_km:   teig.nearest_grid_distance_km,
      },
      grid_distance_km:              teig.nearest_grid_distance_km,
      voltage_level_kv:              gridVoltage,
      nearest_protected_distance_km: teig.nearest_protected_distance_km,
      // Port:
      port_distance_km:              teig.nearest_port_distance_km,
      port_name:                     teig.nearest_port_name,
      nearest_port:                  teig.nearest_port_name,
      port_depth_m:                  teig.nearest_port_depth_m,
      port_depth_estimated:          teig.nearest_port_depth_estimated ?? false,
      // Water — map backend nearest_water_* to fields AnalysisPanel reads:
      water_distance_km:             teig.nearest_water_distance_km,
      river_name:   teig.nearest_water_type === 'elv'    ? (teig.nearest_water_name || '') : null,
      lake_name:    teig.nearest_water_type === 'innsjø' ? (teig.nearest_water_name || '') : null,
      // Coast is also a "nearest source" candidate when the parcel
      // sits on the shoreline (e.g. coastal island). Backend sets
      // water_type='sjø' / water_name='Sjø' when the coastline KNN
      // wins. Surface it under the same Verified Site Data row so
      // a parcel literally surrounded by sea stops showing
      // "Aksdalsvatnet · 11 km".
      sea_name:     teig.nearest_water_type === 'sjø'    ? (teig.nearest_water_name || 'Sjø') : null,
      water_source_type:             teig.nearest_water_distance_km != null ? 'freshwater' : null,
      is_salmon_river:               teig.is_salmon_river ?? false,
      has_salmon:                    teig.is_salmon_river ?? false,
      has_water_concessions:         teig.is_protected_watershed ?? false,
      // Zone:
      zoning_score:                  teig.zone_suitability ?? 0.5,
      zoning_info: {
        zone_code:        teig.zone_code,
        zone_name:        teig.zone_name,
        zone_suitability: teig.zone_suitability,
      },
      // Structured zone + location assessment so AnalysisPanel reads the same fields
      // regardless of whether the site came from SSE list or map click.
      _zone_result: {
        zone_code:        teig.zone_code,
        zone_name:        teig.zone_name,
        zone_suitability: teig.zone_suitability,
        area_ha:          teig.total_area_ha ?? teig.areal_ha,
        municipality:     teig.kommunenummer,
        location:         { latitude: teig.lat, longitude: teig.lon },
        nearest_grid: {
          id:            teig.nearest_grid_id,
          name:          gridName,
          voltage_level: gridVoltage,
          price_zone:    gridPriceZone,
          distance_km:   teig.nearest_grid_distance_km,
        },
      },
      _location_assessment: {
        protected_dist_km:    teig.nearest_protected_distance_km,
        port_name:            teig.nearest_port_name,
        port_dist_km:         teig.nearest_port_distance_km,
        port_depth_m:         teig.nearest_port_depth_m,
        port_depth_estimated: teig.nearest_port_depth_estimated ?? false,
        water_dist_km:        teig.nearest_water_distance_km,
        flood_zone:           teig.flood_zone,
        landslide_zone:       teig.landslide_zone,
        slope_pct:            teig.slope_pct,
      },
      // Hazards:
      flood_zone:            teig.flood_zone,
      flood_hard_stop:       teig.flood_zone ?? false,
      kvikkleire_hard_stop:  teig.kvikkleire_hard_stop ?? false,
      kvikkleire:            teig.kvikkleire,
      nin_type_a:            teig.nin_type_a ?? false,
      nin_type_a_name:       teig.nin_type_a_name,
      // Terrain (slow enrichment — may be null if filter not active):
      elevation_m:                   teig.elevation_m,
      road_category:                 teig.road_category,
      bridge_weight_limit_tonnes:    teig.bridge_weight_limit_t,
      construction_season_months:    teig.frost_free_months,
      // Environment:
      red_listed_species_count:      teig.red_listed_count ?? 0,
      har_kulturminne:               teig.har_kulturminne,
      // Cadastral:
      tvist:                         teig.tvist,
      har_tvist:                     teig.tvist,
      total_parcel_area_ha:          teig.total_area_ha ?? teig.areal_ha,
      // Municipality for price zone lookup:
      municipality_number:           teig.kommunenummer,
      // Pre-computed LCOH from /search:
      lcoh_usd_kg:                   teig.lcoh_usd_kg,
      grid_capex_mnok:               teig.grid_capex_mnok,
      electricity_price_nok_mwh:     teig.electricity_price_nok_mwh,
      // Flag so AnalysisPanel skips "Kapasitet ikke satt" note:
      _from_search:                  true,
    };

    const gridScore = (teig.nearest_grid_distance_km ?? 5) <= 1 ? 5
      : (teig.nearest_grid_distance_km ?? 5) <= 3 ? 4
      : (teig.nearest_grid_distance_km ?? 5) <= 5 ? 3 : 2;
    const voltageScore = (teig.nearest_grid_voltage_kv ?? 66) >= 300 ? 5
      : (teig.nearest_grid_voltage_kv ?? 66) >= 132 ? 4
      : (teig.nearest_grid_voltage_kv ?? 66) >= 66 ? 3 : 2;
    const protectedScore = (teig.nearest_protected_distance_km ?? 5) >= 5 ? 5
      : (teig.nearest_protected_distance_km ?? 5) >= 2 ? 4
      : (teig.nearest_protected_distance_km ?? 5) >= 1 ? 3 : 2;
    const zoneScore = teig.zone_suitability ?? 0.5;

    setClickedPoint({ longitude: teig.lon, latitude: teig.lat });
    setAnalysisResult({
      location: { latitude: teig.lat, longitude: teig.lon },
      suitability_score: (zoneScore + gridScore / 5 + voltageScore / 5 + protectedScore / 5) / 4,
      summary: `Tomt ${teig.matrikkelnummer || teig.teig_id} — ${(teig.total_area_ha ?? teig.areal_ha)?.toFixed(1)} ha${teig.constituent_count > 1 ? ` (${teig.constituent_count} teiger)` : ''}, ${teig.zone_name || teig.zone_code || 'unknown zone'}`,
      parameters: {
        grid_distance: {
          name: "Grid Distance", score: gridScore,
          value: `${teig.nearest_grid_distance_km?.toFixed(1) ?? '?'} km`,
          rating: gridScore >= 4 ? 'Excellent' : gridScore >= 3 ? 'Good' : 'Moderate',
          details: `${teig.nearest_grid_distance_km?.toFixed(1) ?? '?'} km til ${teig.nearest_grid_name || 'stasjon'} (${teig.nearest_grid_voltage_kv ?? '?'} kV)`,
          is_showstopper: false,
        },
        protected_area_proximity: {
          name: "Protected Area Proximity", score: protectedScore,
          value: `${teig.nearest_protected_distance_km?.toFixed(1) ?? '?'} km`,
          rating: protectedScore >= 4 ? 'Excellent' : protectedScore >= 3 ? 'Good' : protectedScore >= 2 ? 'Moderate' : 'Critical',
          details: `${teig.nearest_protected_distance_km?.toFixed(1) ?? '?'} km til nærmeste verneområde`,
          is_showstopper: false,
        },
        grid_strength: {
          name: "Grid Strength", score: voltageScore,
          value: `${teig.nearest_grid_voltage_kv ?? '?'} kV`,
          rating: voltageScore >= 4 ? 'Excellent' : voltageScore >= 3 ? 'Good' : 'Moderate',
          details: `${teig.nearest_grid_voltage_kv ?? '?'} kV nett ved ${teig.nearest_grid_name || 'stasjon'}`,
          is_showstopper: false,
        },
        zoning: {
          name: "Zoning", score: Math.round(zoneScore * 5),
          value: teig.zone_code ? `${teig.zone_code} — ${teig.zone_name}` : 'Unknown zone',
          rating: zoneScore >= 0.9 ? 'Excellent' : zoneScore >= 0.7 ? 'Good' : zoneScore >= 0.4 ? 'Moderate' : 'Poor',
          details: `${teig.zone_name || 'Unknown zone'} (${teig.zone_code || '?'}). Areal: ${(teig.total_area_ha ?? teig.areal_ha)?.toFixed(1) ?? '?'} ha${teig.constituent_count > 1 ? ` (${teig.constituent_count} parcels combined)` : ''}`,
          is_showstopper: false,
        },
        port_distance: {
          name: "Port Distance", score: (teig.nearest_port_distance_km ?? 20) <= 5 ? 5 : (teig.nearest_port_distance_km ?? 20) <= 10 ? 4 : 3,
          value: teig.nearest_port_distance_km ? `${teig.nearest_port_distance_km.toFixed(1)} km` : 'N/A',
          rating: (teig.nearest_port_distance_km ?? 99) <= 5 ? 'Excellent' : 'Good',
          details: teig.nearest_port_name ? `${teig.nearest_port_name} — ${teig.nearest_port_distance_km?.toFixed(1) ?? '?'} km` : 'Ingen havnedata',
          is_showstopper: false,
        },
      },
      _site:             siteObj,
      total_parcel_area_ha: teig.total_area_ha ?? teig.areal_ha,
      grid_connections:  [siteObj.nearest_grid],
      environmental_constraints: [],
      nearby_infrastructure: [],
      energy_resources:  [],
      nearest_ports:     teig.nearest_port_name ? [{ name: teig.nearest_port_name, distance_km: teig.nearest_port_distance_km }] : [],
      nearest_co2_source: teig.nearest_co2_source || null,
    });

    // Auto-select the assemblage geometry as a parcel (shows on map in cyan)
    if (teig.geometry) {
      const id = teig.teig_id || teig.matrikkelnummer || 'assemblage';
      const area_ha = teig.total_area_ha ?? teig.areal_ha ?? 0;
      setSelectedParcels([{ id, area_ha, geometry: teig.geometry }]);
    } else {
      setSelectedParcels([]);
    }

    // Fetch slope + elevation at teig centroid if not already in search result.
    // For FI sites this is the ONLY place elevation is fetched (the SSE search
    // skips bulk OpenTopoData calls due to rate limits — see commit 5a.1).
    // Read country from the ref because handleSearchTeigSelect is memoised
    // with [] deps and would otherwise capture the initial 'NO' value.
    if ((teig.slope_pct == null || teig.elevation_m == null) && teig.lat && teig.lon) {
      const apiBase = import.meta.env.VITE_API_URL || '';
      sitesApi.analyzeArea(teig.lat, teig.lon, selectedCountryRef.current)
        .then(({ data }) => {
          // Update if EITHER slope or elevation came back. Slope can be null
          // (rate-limited 4/5 samples) while elevation is still valid.
          if (data?.slope_pct != null || data?.elevation_m != null) {
            setAnalysisResult(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                _site: {
                  ...prev._site,
                  slope_pct: data.slope_pct ?? prev._site.slope_pct,
                  elevation_m: data.elevation_m ?? prev._site.elevation_m,
                  _location_assessment: {
                    ...prev._site._location_assessment,
                    slope_pct: data.slope_pct ?? prev._site._location_assessment?.slope_pct,
                  },
                },
              };
            });
          }
        })
        .catch(() => {});
    }

    // Search result — never show the "Kapasitet ikke satt" note
    setDefaultMwNote(false);
    setTeigEnriching(false);
    setEnrichedTeigIds(new Set());
    enrichedTeigIdsRef.current = new Set();

    // WMS fallback: if zone_code wasn't resolved by backend DIBK batch, query live
    if (!teig.zone_code) {
      queryZoningAtCoords(teig.lon, teig.lat)
        .then(zone => { if (zone) applyZoneToAnalysis(zone); })
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load CO2 sources once on mount
  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBase}/api/v1/layers/co2-sources`)
      .then(r => r.json())
      .then(data => setLayers(prev => ({
        ...prev,
        co2Sources: { ...prev.co2Sources, data },
      })))
      .catch(() => {});
  }, []);

  const computeNearestCo2 = useCallback((lng, lat) => {
    const co2Data = layers.co2Sources?.data;
    if (!co2Data?.features?.length) return null;
    const pt = turf.point([lng, lat]);
    const nearest = turf.nearestPoint(pt, co2Data);
    const dist = turf.distance(pt, nearest, { units: 'kilometers' });
    return {
      name: nearest.properties.name,
      sector: nearest.properties.sector,
      operator: nearest.properties.operator,
      distance_km: Math.round(dist * 10) / 10,
      co2_kt: nearest.properties.co2_kt,
    };
  }, [layers.co2Sources]);

  return (
    <div className="site-screener">
      {/* PR-4: map-zoom-locked is a soft signal — demo CSS in PR-5
          paints it with filter:blur(8px) + a "book a demo to zoom in"
          overlay. We do not setMaxZoom; the visual hint is enough. */}
      <div
        className={
          'map-container' +
          (maxZoomBeforeBlur != null && viewState.zoom > maxZoomBeforeBlur
            ? ' map-zoom-locked'
            : '')
        }
      >
        <Map
          ref={mapRef}
          {...viewState}
          onMove={handleMapMove}
          onLoad={onMapLoad}
          onClick={onSiteClick || handleMapClick}
          onMouseMove={onHover}
          onMouseLeave={onMouseLeave}
          // PR-4: when layerWhitelist is set, only those layer ids are
          // click-interactive. Default (null) keeps the full set so the
          // authed app behaves identically.
          interactiveLayerIds={(layerWhitelist
            ? (
                [
                  'grid-connections',
                  'environmental-constraints',
                  'environmental-constraints-outline',
                  'ports-circle',
                  'ports-labels',
                  'cadastral-parcels',
                  'cadastral-parcels-outline',
                  'nearby-parcels-good-outline',
                  'nearby-parcels-bad-outline',
                  'search-teigs-fill',
                  'dh-plants-circles',
                  'industrial-areas',
                  'industrial-areas-outline',
                  'co2-sources-circles',
                  'ultranet-sites-circles',
                  'ultranet-backbone-lines',
                ].filter(id => layerWhitelist.includes(id))
              )
            : [
                'grid-connections',
                'environmental-constraints',
                'environmental-constraints-outline',
                'ports-circle',
                'ports-labels',
                'cadastral-parcels',
                'cadastral-parcels-outline',
                'nearby-parcels-good-outline',
                'nearby-parcels-bad-outline',
                'nearby-parcels-good-hit',
                'nearby-parcels-bad-hit',
                'search-teigs-fill',
                'dh-plants-circles',
                'industrial-areas',
                'industrial-areas-outline',
                'co2-sources-circles',
                'ultranet-sites-circles',
                'ultranet-backbone-lines',
              ])}
          mapStyle={mapStyle}
          mapboxAccessToken={MAPBOX_TOKEN}
          cursor={parcelSelectMode ? 'crosshair' : undefined}
        >
          {/* Grid Connections Layer */}
          {layers.gridConnections.visible && layers.gridConnections.data && (
            <Source type="geojson" data={layers.gridConnections.data}>
              <Layer
                id="grid-connections"
                type="circle"
                minzoom={6}
                paint={{
                  'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['coalesce', ['get', 'voltage_level'], 0],
                    0, 3,
                    32, 4,
                    132, 6,
                    300, 8,
                    525, 10
                  ],
                  // Color by voltage_level (kV) — a size proxy for substation
                  // scale, since actual capacity_mw is only populated for a
                  // subset of NO records. Distribution (low kV, small) → green,
                  // sub-transmission → yellow/orange, transmission (high kV,
                  // large) → red/purple. Unknown voltage stays gray.
                  'circle-color': [
                    'case',
                    ['==', ['get', 'voltage_level'], null], '#94a3b8',
                    [
                      'interpolate', ['linear'], ['get', 'voltage_level'],
                      0,   '#86efac',
                      30,  '#fde047',
                      70,  '#fb923c',
                      150, '#f87171',
                      300, '#b91c1c',
                      525, '#7c2d92'
                    ]
                  ],
                  'circle-opacity': [
                    'case',
                    ['==', ['get', 'voltage_level'], null], 0.5, // More transparent for unknown-size points
                    0.9
                  ],
                  'circle-stroke-width': 2,
                  'circle-stroke-color': '#ffffff'
                }}
              />
            </Source>
          )}

          {/* DH Plants Layer (FI) */}
          {layers.dhPlants?.visible && layers.dhPlants?.data && (
            <Source type="geojson" data={layers.dhPlants.data}>
              <Layer
                id="dh-plants-circles"
                type="circle"
                minzoom={8}
                paint={{
                  'circle-radius': ['interpolate', ['linear'], ['coalesce', ['get', 'heat_output_mw'], 5], 0, 4, 20, 7, 100, 10, 500, 14],
                  'circle-color': '#f97316',
                  'circle-opacity': 0.85,
                  'circle-stroke-width': 1.5,
                  'circle-stroke-color': '#ffffff',
                }}
              />
            </Source>
          )}

          {/* PeeringDB facilities Layer (data center use-case).
              Toggled from LayerControls → "Peering Facilities". IXPs
              render as larger teal circles, colocation facilities as
              smaller purple circles. Data is FeatureCollection with a
              `kind: "ixp" | "colo"` property the paint expression keys
              on. Backend caches the PeeringDB call for 24h. */}
          {activeWmsLayers.peering_facilities && peeringFacilitiesData && (
            <Source id="peering-facilities-src" type="geojson" data={peeringFacilitiesData}>
              <Layer
                id="peering-facilities-circles"
                type="circle"
                minzoom={5}
                paint={{
                  'circle-radius': ['case', ['==', ['get', 'kind'], 'ixp'], 9, 6],
                  'circle-color':  ['case', ['==', ['get', 'kind'], 'ixp'], '#06b6d4', '#a855f7'],
                  'circle-opacity': 0.85,
                  'circle-stroke-width': 1.5,
                  'circle-stroke-color': '#ffffff',
                }}
              />
              <Layer
                id="peering-facilities-labels"
                type="symbol"
                minzoom={9}
                layout={{
                  'text-field':  ['get', 'name'],
                  'text-size':   11,
                  'text-offset': [0, 1.2],
                  'text-anchor': 'top',
                  'text-allow-overlap': false,
                }}
                paint={{
                  'text-color':       '#e2e8f0',
                  'text-halo-color':  '#0f172a',
                  'text-halo-width':  1.4,
                }}
              />
            </Source>
          )}

          {/* Ultranet Italia partner backbone routes — drawn under the
              carrier site markers so points stay clickable on top. */}
          {activeWmsLayers.ultranet_backbone && ultranetBackboneData && (
            <Source id="ultranet-backbone-src" type="geojson" data={ultranetBackboneData}>
              <Layer
                id="ultranet-backbone-lines"
                type="line"
                paint={{
                  'line-color': '#dc2626', // "Dorsali Ultranet" red, matching original map
                  'line-width': 2,
                  'line-opacity': 0.75,
                }}
              />
            </Source>
          )}

          {/* Ultranet Italia partner carrier network — Centraline
              (cabinets) as smaller dots, Infrastrutture (PoP sites) as
              larger dots. Used to find DC sites near partner fiber
              infrastructure. */}
          {activeWmsLayers.ultranet_carrier_sites && ultranetSitesData && (
            <Source id="ultranet-sites-src" type="geojson" data={ultranetSitesData}>
              <Layer
                id="ultranet-sites-circles"
                type="circle"
                minzoom={5}
                paint={{
                  'circle-radius': ['case', ['==', ['get', 'site_type'], 'infrastruttura'], 8, 5],
                  // Matches the original Ultranet map's legend colors:
                  // "Infrastrutture Ultranet" dark blue, "Centraline Ultranet" orange.
                  'circle-color':  ['case', ['==', ['get', 'site_type'], 'infrastruttura'], '#194a95', '#ffba00'],
                  'circle-opacity': 0.85,
                  'circle-stroke-width': 1.5,
                  'circle-stroke-color': '#ffffff',
                }}
              />
              <Layer
                id="ultranet-sites-labels"
                type="symbol"
                minzoom={10}
                layout={{
                  'text-field':  ['get', 'name'],
                  'text-size':   11,
                  'text-offset': [0, 1.2],
                  'text-anchor': 'top',
                  'text-allow-overlap': false,
                }}
                paint={{
                  'text-color':       '#e2e8f0',
                  'text-halo-color':  '#0f172a',
                  'text-halo-width':  1.4,
                }}
              />
            </Source>
          )}

          {/* Environmental Constraints Layer */}
          {layers.environmentalConstraints.visible && layers.environmentalConstraints.data && (
            <Source type="geojson" data={layers.environmentalConstraints.data}>
              <Layer
                id="environmental-constraints"
                type="fill"
                minzoom={10}
                paint={{
                  'fill-color': '#dc2626',
                  'fill-opacity': 0.3
                }}
              />
              <Layer
                id="environmental-constraints-outline"
                type="line"
                minzoom={10}
                paint={{
                  'line-color': '#dc2626',
                  'line-width': 2
                }}
              />
            </Source>
          )}

          {/* Ports/Harbors Layer — hydrogen-only.
              Gated on useCase here AND scoped via useCases:["hydrogen"]
              in layers.js so the toggle is hidden from DC users. The
              extra render gate handles the cross-session case: a user
              who turned the layer on in hydrogen mode and switched to
              DC shouldn't keep seeing the port markers. */}
          {useCase !== 'data_center' && layers.ports && layers.ports.visible && layers.ports.data && (
            <Source type="geojson" data={layers.ports.data}>
              {/* Port markers as circles */}
              <Layer
                id="ports-circle"
                type="circle"
                minzoom={6}
                layout={{}}
                paint={{
                  'circle-radius': [
                    'case',
                    ['==', ['get', 'system_name'], 'PORT_FACILITY'], 8,
                    ['==', ['get', 'system_name'], 'HARBOUR'], 6,
                    5
                  ],
                  'circle-color': '#06b6d4',
                  'circle-stroke-width': 1.5,
                  'circle-stroke-color': '#0e7490'
                }}
              />
              <Layer
                id="ports-labels"
                type="symbol"
                minzoom={9}
                layout={{
                  'text-field': ['get', 'name'],
                  'text-size': 12,
                  'text-offset': [0, 2.0],
                  'text-anchor': 'top',
                  'text-optional': true
                }}
                paint={{
                  'text-color': '#0891b2',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 2
                }}
              />
            </Source>
          )}

          {/* CO2 Sources Layer — hydrogen-only.
              Gated on useCase here AND scoped via useCases:["hydrogen"]
              in layers.js so the toggle is hidden from DC users. The
              extra render gate handles the cross-session case: a user
              who turned the layer on in hydrogen mode and switched to
              DC shouldn't keep seeing the markers. */}
          {useCase !== 'data_center' && layers.co2Sources?.visible && layers.co2Sources?.data && (
            <Source id="co2-sources-source" type="geojson" data={layers.co2Sources.data}>
              <Layer
                id="co2-sources-circles"
                type="circle"
                paint={{
                  'circle-radius': [
                    'interpolate', ['linear'], ['get', 'co2_kt'],
                    50, 7, 500, 12, 2000, 20
                  ],
                  'circle-color': '#ef4444',
                  'circle-opacity': 0.85,
                  'circle-stroke-width': 2,
                  'circle-stroke-color': '#fff',
                }}
              />
              <Layer
                id="co2-sources-labels"
                type="symbol"
                minzoom={7}
                layout={{
                  'text-field': ['get', 'name'],
                  'text-size': 10,
                  'text-anchor': 'top',
                  'text-offset': [0, 1.2],
                  'text-optional': true,
                }}
                paint={{
                  'text-color': '#fca5a5',
                  'text-halo-color': '#0f172a',
                  'text-halo-width': 1.5,
                }}
              />
            </Source>
          )}

          {/* Zoning WMS Layers - always mounted, visibility controlled via layout */}
          <Source
            id="kommuneplan-wms-source"
            type="raster"
            tiles={[
              `https://nap.ft.dibk.no/services/wms/kommuneplaner/?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image/png&TRANSPARENT=true&LAYERS=arealformal_kp,arealformal_kdp&CRS=EPSG:3857&STYLES=&WIDTH=256&HEIGHT=256&BBOX={bbox-epsg-3857}`
            ]}
            tileSize={256}
          >
            <Layer
              id="kommuneplan-layer"
              type="raster"
              layout={{ visibility: layers.zoning?.visible ? 'visible' : 'none' }}
              paint={{ 'raster-opacity': 0.6 }}
            />
          </Source>
          <Source
            id="reguleringsplan-wms-source"
            type="raster"
            tiles={[
              `https://nap.ft.dibk.no/services/wms/reguleringsplaner/?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image/png&TRANSPARENT=true&LAYERS=arealformal_vn1,arealformal_vn2,arealformal_vn3&CRS=EPSG:3857&STYLES=&WIDTH=256&HEIGHT=256&BBOX={bbox-epsg-3857}`
            ]}
            tileSize={256}
          >
            <Layer
              id="reguleringsplan-layer"
              type="raster"
              layout={{ visibility: layers.zoning?.visible ? 'visible' : 'none' }}
              paint={{ 'raster-opacity': 0.75 }}
            />
          </Source>

          {/* Property Boundaries (Matrikkelen) */}
          <Source
            id="parcels-wms-source"
            type="raster"
            tiles={[
              `https://wms.geonorge.no/skwms1/wms.matrikkel?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image/png&TRANSPARENT=true&LAYERS=eiendomsgrense,eiendoms_id&CRS=EPSG:3857&STYLES=&WIDTH=256&HEIGHT=256&BBOX={bbox-epsg-3857}`
            ]}
            tileSize={256}
          >
            <Layer
              id="parcels-layer"
              type="raster"
              minzoom={13}
              layout={{ visibility: layers.parcels?.visible ? 'visible' : 'none' }}
              paint={{ 'raster-opacity': 0.85 }}
            />
          </Source>

          {/* Nearby Parcels — green/red by zone + flags + all project criteria */}
          {nearbyParcels?.features?.length > 0 && (() => {
            const unselected = nearbyParcels.features.filter(f => f !== selectedTeig);
            // isGreen: teig-level evaluation — all criteria measured from teig centroid.
            // Phase 1 (substation filtering) uses only min_voltage in SQL.
            // Everything here is Phase 2 teig-level classification.
            // isGreen: teig-level evaluation, all criteria measured from the
            // teig centroid. The rule lives in map/HoverCards.parcelVerdict so
            // the hover card can say why a parcel is red.
            const isGreen = f => parcelVerdict(f.properties || {}, teigCriteria).ok;
            const good = unselected.filter(f => isGreen(f));
            const bad  = unselected.filter(f => !isGreen(f));
            return (
              <>
                {/* Teig outlines — full cadastral parcel boundary */}
                <Source id="nearby-parcels-good-source" type="geojson" data={{ type: 'FeatureCollection', features: good }}>
                  {/* Invisible fill: hovering anywhere inside a parcel shows its card, not only on the outline */}
                  <Layer id="nearby-parcels-good-hit" type="fill" paint={{ 'fill-color': '#000000', 'fill-opacity': 0 }} />
                  <Layer id="nearby-parcels-good-outline" type="line" paint={{ 'line-color': '#22c55e', 'line-width': 2 }} />
                </Source>
                <Source id="nearby-parcels-bad-source" type="geojson" data={{ type: 'FeatureCollection', features: bad }}>
                  <Layer id="nearby-parcels-bad-hit" type="fill" paint={{ 'fill-color': '#000000', 'fill-opacity': 0 }} />
                  <Layer id="nearby-parcels-bad-outline" type="line" paint={{ 'line-color': '#ef4444', 'line-width': 1.5 }} />
                </Source>
                {/* Zone intersection fills — actual DIBK plan zone area within each teig */}
                <Source id="zone-intersections-good-source" type="geojson" data={zoneIntersections.good}>
                  <Layer id="zone-intersections-good-fill" type="fill" paint={{ 'fill-color': '#22c55e', 'fill-opacity': 0.35 }} />
                </Source>
                <Source id="zone-intersections-bad-source" type="geojson" data={zoneIntersections.bad}>
                  <Layer id="zone-intersections-bad-fill" type="fill" paint={{ 'fill-color': '#ef4444', 'fill-opacity': 0.2 }} />
                </Source>
                {/* Enriched-teig halo — subtle blue glow on teiger that have been analysed */}
                {enrichedTeigIds.size > 0 && (() => {
                  const enrichedFeatures = nearbyParcels.features.filter(f => {
                    const tid = f.properties?.teigid || f.properties?.matrikkelnummer;
                    return tid && enrichedTeigIds.has(tid);
                  });
                  if (!enrichedFeatures.length) return null;
                  return (
                    <Source id="enriched-teig-halo-source" type="geojson"
                      data={{ type: 'FeatureCollection', features: enrichedFeatures }}>
                      <Layer id="enriched-teig-halo"
                        type="line"
                        paint={{ 'line-color': '#60a5fa', 'line-width': 4, 'line-opacity': 0.6 }}
                      />
                    </Source>
                  );
                })()}
              </>
            );
          })()}

          {/* Search Teig Results — cyan outline polygons from /search SSE */}
          {searchTeigs.length > 0 && (
            <Source
              id="search-teigs-source"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: searchTeigs
                  .map((t, i) => [t, i])
                  .filter(([t]) => t.geometry)
                  .map(([t, i]) => ({
                    type: 'Feature',
                    geometry: t.geometry,
                    properties: {
                      // the hover card reads searchTeigs[_idx]: feature
                      // properties cannot carry the nested screen facts
                      _idx: i,
                      teig_id: t.teig_id,
                      zone_suitability: t.zone_suitability ?? 0.5,
                      readiness_score: t.readiness_score ?? null,
                      area_ha: t.total_area_ha ?? t.areal_ha ?? 0,
                      grid_kv: t.nearest_grid_voltage_kv ?? 0,
                      grid_km: t.nearest_grid_distance_km ?? 99,
                    },
                  })),
              }}
            >
              {/* Fill color by zone suitability: green = good, yellow = ok, red = bad */}
              <Layer id="search-teigs-fill" type="fill"
                paint={{
                  'fill-color': [
                    'interpolate', ['linear'], ['get', 'zone_suitability'],
                    0.0, '#ef4444',   // red — bad zone
                    0.4, '#fb923c',   // orange
                    0.6, '#fbbf24',   // yellow
                    0.8, '#22c55e',   // green — good zone
                  ],
                  'fill-opacity': 0.35,
                }} />
              <Layer id="search-teigs-outline" type="line"
                paint={{
                  'line-color': [
                    'interpolate', ['linear'], ['get', 'zone_suitability'],
                    0.0, '#ef4444',
                    0.4, '#fb923c',
                    0.6, '#fbbf24',
                    0.8, '#22c55e',
                  ],
                  'line-width': 2,
                }} />
            </Source>
          )}

          {/* Harbour-screen results: the result polygon is the part of the
              parcel that meets every rule; this dashed line is the whole
              registered parcel around it. */}
          {searchTeigs.some(t => t.harbour_screen?.parcel_geometry) && (
            <Source
              id="search-teigs-parcel-source"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: searchTeigs
                  .filter(t => t.harbour_screen?.parcel_geometry)
                  .map(t => ({ type: 'Feature', geometry: t.harbour_screen.parcel_geometry, properties: { teig_id: t.teig_id } })),
              }}
            >
              <Layer id="search-teigs-parcel-outline" type="line"
                paint={{ 'line-color': '#e2e8f0', 'line-width': 1.2, 'line-opacity': 0.8, 'line-dasharray': [2, 2] }} />
            </Source>
          )}

          {/* Selected Teig — outline on full parcel, fill on zone intersection */}
          {selectedTeig && (
            <>
              <Source id="selected-teig-source" type="geojson" data={selectedTeig}>
                <Layer id="selected-teig-outline" type="line"
                  paint={{ 'line-color': '#22c55e', 'line-width': 2.5 }} />
              </Source>
              <Source id="selected-teig-zone-source" type="geojson"
                data={selectedZoneIntersection || selectedTeig}>
                <Layer id="selected-teig-fill" type="fill"
                  paint={{ 'fill-color': '#22c55e', 'fill-opacity': 0.4 }} />
              </Source>
            </>
          )}

          {/* Precise buildable area — green hatched overlay shown
              when AnalysisPanel has computed the dense 10×10 / 15×15
              DEM grid for the currently-open parcel. Lets the user
              see WHERE the buildable headline came from, not just
              the number. */}
          {buildablePolygon && (
            <Source id="buildable-polygon-source" type="geojson"
              data={{ type: 'Feature', geometry: buildablePolygon, properties: {} }}>
              <Layer id="buildable-polygon-fill" type="fill"
                paint={{ 'fill-color': '#10b981', 'fill-opacity': 0.45 }} />
              <Layer id="buildable-polygon-outline" type="line"
                paint={{ 'line-color': '#10b981', 'line-width': 1.5, 'line-opacity': 0.9 }} />
            </Source>
          )}

          {/* Selected Parcels Highlight */}
          <Source
            id="selected-parcels-source"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: selectedParcels.map(p => ({ type: 'Feature', geometry: p.geometry, properties: {} }))
            }}
          >
            <Layer id="selected-parcels-fill" type="fill"
              paint={{ 'fill-color': '#22d3ee', 'fill-opacity': 0.3 }} />
            <Layer id="selected-parcels-outline" type="line"
              paint={{ 'line-color': '#22d3ee', 'line-width': 2.5 }} />
          </Source>

          {/* Saved Sites */}
          {savedSites.map(site => (
            <Marker
              key={site.id}
              longitude={site.location.longitude}
              latitude={site.location.latitude}
              color={getSuitabilityColor(site.suitability_score)}
            />
          ))}

          {/* Site → feature links: dashed lines + labelled endpoints */}
          {analysisResult?.location && (siteLinks.substation || siteLinks.port || siteLinks.focus) && (() => {
            const from = [analysisResult.location.longitude, analysisResult.location.latitude];
            const links = [
              siteLinks.substation ? { ...siteLinks.substation, color: '#3b82f6', id: 'substation' } : null,
              siteLinks.port       ? { ...siteLinks.port,       color: '#10b981', id: 'port' } : null,
              siteLinks.focus      ? { ...siteLinks.focus,      color: '#f59e0b', id: 'focus' } : null,
            ].filter(l => l && Number.isFinite(Number(l.lat)) && Number.isFinite(Number(l.lon)))
             .map(l => ({ ...l, lat: Number(l.lat), lon: Number(l.lon) }));
            const fc = { type: 'FeatureCollection', features: links.map(l => ({
              type: 'Feature', properties: { color: l.color },
              geometry: { type: 'LineString', coordinates: [from, [l.lon, l.lat]] },
            })) };
            return (
              <>
                <Source id="site-links-src" type="geojson" data={fc}>
                  <Layer id="site-links-line" type="line"
                         layout={{ 'line-cap': 'round' }}
                         paint={{ 'line-color': ['get', 'color'], 'line-width': 2.5, 'line-dasharray': [2, 2], 'line-opacity': 0.95 }} />
                </Source>
                {links.map(l => (
                  <Marker key={l.id} longitude={l.lon} latitude={l.lat} anchor="bottom">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
                      <div style={{ background: 'rgba(15,23,42,0.92)', color: l.color, border: `1px solid ${l.color}`,
                                    borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600,
                                    whiteSpace: 'nowrap', marginBottom: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                        {l.label}
                      </div>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', background: l.color,
                                    border: '2px solid #fff', boxShadow: `0 0 0 4px ${l.color}55` }} />
                    </div>
                  </Marker>
                ))}
              </>
            );
          })()}

          {/* Clicked Point */}
          {clickedPoint && (
            <Marker
              longitude={clickedPoint.longitude}
              latitude={clickedPoint.latitude}
              color="#22d3ee"
              scale={1.2}
            />
          )}


          {/* Hover cards (map/HoverCards): search results, substations, parcels */}
          {hoverInfo && !drawActive && CARD_LAYERS.has(hoverInfo.feature?.layer?.id) && (() => {
            const lid = hoverInfo.feature.layer.id;
            const props = hoverInfo.feature.properties || {};
            let card = null;
            if (lid === 'grid-connections') {
              card = <SubstationHoverCard properties={props} />;
            } else if (lid === 'search-teigs-fill') {
              const site = searchTeigs[Number(props._idx)];
              if (site) card = <SiteHoverCard site={site} rank={searchRank.get(site)} total={searchTeigs.length} />;
            } else {
              card = <ParcelHoverCard properties={props} criteria={teigCriteria} />;
            }
            if (!card) return null;
            return (
              <div className="map-hover"
                style={{ left: hoverInfo.x, top: hoverInfo.y, ...hoverPlacement(hoverInfo.x, hoverInfo.y, hoverInfo.w) }}>
                {card}
              </div>
            );
          })()}

          {/* Hover Tooltip — suppressed while drawing so the cursor stays unobstructed */}
          {hoverInfo && !drawActive && !CARD_LAYERS.has(hoverInfo.feature?.layer?.id) && (
            <div
              className="hover-tooltip"
              style={{
                position: 'absolute',
                left: hoverInfo.x,
                top: hoverInfo.y,
                pointerEvents: 'none'
              }}
            >
              {(hoverInfo.feature?.layer.id === 'environmental-constraints' ||
                hoverInfo.feature?.layer.id === 'environmental-constraints-outline') && (
                <div className="tooltip-content">
                  <div className="tooltip-title">Verneområde</div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Navn:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.name || 'Unknown'}
                    </span>
                  </div>
                  {hoverInfo.feature.properties.protection_level && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Type:</span>
                      <span className="tooltip-value">
                        {hoverInfo.feature.properties.protection_level}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {hoverInfo.feature?.layer.id === 'ultranet-sites-circles' && (
                <div className="tooltip-content">
                  <div className="tooltip-title">
                    {hoverInfo.feature.properties.name || 'Ultranet Site'}
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Type:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.site_type === 'infrastruttura'
                        ? 'Infrastruttura (PoP)' : 'Centralina (cabinet)'}
                    </span>
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Comune:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.comune}
                    </span>
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Provincia:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.provincia}
                    </span>
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Partner:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.partner}
                    </span>
                  </div>
                </div>
              )}
              {hoverInfo.feature?.layer.id === 'ultranet-backbone-lines' && (
                <div className="tooltip-content">
                  <div className="tooltip-title">
                    {hoverInfo.feature.properties.name || 'Ultranet Backbone'}
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Partner:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.partner}
                    </span>
                  </div>
                </div>
              )}
              {(hoverInfo.feature?.layer.id === 'ports-circle' ||
                hoverInfo.feature?.layer.id === 'ports-labels') && (
                <div className="tooltip-content">
                  {(() => {
                    const pp = hoverInfo.feature.properties || {};
                    const Row = ({ label, value }) => (value == null || value === '' ? null : (
                      <div className="tooltip-item">
                        <span className="tooltip-label">{label}:</span>
                        <span className="tooltip-value">{value}</span>
                      </div>
                    ));
                    const place = [pp.municipality, pp.county].filter(Boolean).join(', ');
                    return (<>
                      <div className="tooltip-title">{pp.name}{pp.unlocode ? ` (${pp.unlocode})` : ''}</div>
                      <Row label="Type" value={pp.port_type || pp.system_name} />
                      <Row label="Place" value={place} />
                      <Row label="Depth" value={pp.depth_m != null ? `${pp.depth_m} m (register)` : 'not registered'} />
                      {pp.calls_last_year != null ? (<>
                        <Row label={`Calls ${pp.calls_year}`} value={`${Number(pp.calls_last_year).toLocaleString()} · ${pp.gas_calls || 0} gas · ${pp.tanker_calls || 0} tanker · ${pp.hazmat_calls || 0} hazmat`} />
                        <Row label="Largest" value={[pp.loa_p90_m ? `LOA p90 ${Math.round(pp.loa_p90_m)} m` : null, pp.dwt_max ? `${Number(pp.dwt_max).toLocaleString()} t DWT` : null].filter(Boolean).join(' · ')} />
                        <Row label="Traffic" value={pp.top_classes ? String(pp.top_classes).replace(/_/g, ' ') : null} />
                      </>) : (
                        <Row label="Calls" value="no SafeSeaNet rows for this port" />
                      )}
                    </>);
                  })()}
                  {hoverInfo.feature.properties.owner_type && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Owner:</span>
                      <span className="tooltip-value">
                        {hoverInfo.feature.properties.owner_type}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {hoverInfo.zoning && (
                <div className="tooltip-content">
                  <div className="tooltip-title">{hoverInfo.zoning.name}</div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Zone Code:</span>
                    <span className="tooltip-value">
                      {hoverInfo.zoning.code}
                    </span>
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Plan Type:</span>
                    <span className="tooltip-value">
                      {hoverInfo.zoning.layerType}
                    </span>
                  </div>
                  {hoverInfo.zoning.planId && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Plan ID:</span>
                      <span className="tooltip-value">
                        {hoverInfo.zoning.planId}
                      </span>
                    </div>
                  )}
                  {hoverInfo.zoning.municipality && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Municipality:</span>
                      <span className="tooltip-value">
                        {hoverInfo.zoning.municipality}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {(hoverInfo.feature?.layer?.id === 'cadastral-parcels' ||
                hoverInfo.feature?.layer?.id === 'cadastral-parcels-outline') && (
                <div className="tooltip-content">
                  <div className="tooltip-title">{hoverInfo.feature.properties.property_id}</div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Area:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.area_ha} ha
                    </span>
                  </div>
                  {hoverInfo.feature.properties.municipality && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Municipality:</span>
                      <span className="tooltip-value">
                        {hoverInfo.feature.properties.municipality}
                      </span>
                    </div>
                  )}
                  {hoverInfo.feature.properties.land_use && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Land Use:</span>
                      <span className="tooltip-value">
                        {hoverInfo.feature.properties.land_use}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {hoverInfo.feature?.layer?.id === 'co2-sources-circles' && (
                <div className="tooltip-content">
                  <div className="tooltip-title">{hoverInfo.feature.properties.name}</div>
                  {hoverInfo.feature.properties.co2_kt != null && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Emissions:</span>
                      <span className="tooltip-value" style={{color:'#f59e0b',fontWeight:600}}>
                        {hoverInfo.feature.properties.co2_kt} kt CO₂/yr
                      </span>
                    </div>
                  )}
                  <div className="tooltip-item">
                    <span className="tooltip-label">Operator:</span>
                    <span className="tooltip-value">{hoverInfo.feature.properties.operator}</span>
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Sector:</span>
                    <span className="tooltip-value">{hoverInfo.feature.properties.sector}</span>
                  </div>
                  {hoverInfo.feature.properties.kommune && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Kommune:</span>
                      <span className="tooltip-value">{hoverInfo.feature.properties.kommune}</span>
                    </div>
                  )}
                </div>
              )}
              {(hoverInfo.feature?.layer?.id === 'industrial-areas' ||
                hoverInfo.feature?.layer?.id === 'industrial-areas-outline') && (
                <div className="tooltip-content">
                  <div className="tooltip-title">{hoverInfo.feature.properties.name}</div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Zoning:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.arealformaal_navn} ({hoverInfo.feature.properties.arealformaal})
                    </span>
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">H2 Score:</span>
                    <span className="tooltip-value" style={{
                      color: hoverInfo.feature.properties.h2_score >= 8 ? '#00d4aa' :
                             hoverInfo.feature.properties.h2_score >= 6 ? '#7dd87d' :
                             hoverInfo.feature.properties.h2_score >= 4 ? '#ffd93d' : '#ff6b6b',
                      fontWeight: 'bold'
                    }}>
                      {hoverInfo.feature.properties.h2_score}/10
                    </span>
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Area:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.areal_ha?.toFixed(1)} ha
                    </span>
                  </div>
                  {hoverInfo.feature.properties.municipality && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Municipality:</span>
                      <span className="tooltip-value">
                        {hoverInfo.feature.properties.municipality}
                      </span>
                    </div>
                  )}
                  {hoverInfo.feature.properties.plan_status && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Status:</span>
                      <span className="tooltip-value">
                        {hoverInfo.feature.properties.plan_status}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {/* DH Plants */}
              {hoverInfo.feature?.layer?.id === 'dh-plants-circles' && (
                <div className="tooltip-content">
                  <div className="tooltip-title">District Heating Plant</div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Name:</span>
                    <span className="tooltip-value">{hoverInfo.feature.properties.plant_name || '—'}</span>
                  </div>
                  <div className="tooltip-item">
                    <span className="tooltip-label">Type:</span>
                    <span className="tooltip-value">
                      {hoverInfo.feature.properties.plant_type === 'klk' ? 'Heat-only boiler' :
                       hoverInfo.feature.properties.plant_type === 'lvl' ? 'CHP (condensing)' :
                       hoverInfo.feature.properties.plant_type === 'mlvl' ? 'CHP (back-pressure)' :
                       hoverInfo.feature.properties.plant_type === 'lp' ? 'Heat pump' :
                       hoverInfo.feature.properties.plant_type || '—'}
                    </span>
                  </div>
                  {hoverInfo.feature.properties.heat_output_mw != null && (
                    <div className="tooltip-item">
                      <span className="tooltip-label">Heat output:</span>
                      <span className="tooltip-value">{hoverInfo.feature.properties.heat_output_mw} MW</span>
                    </div>
                  )}
                  <div className="tooltip-item">
                    <span className="tooltip-label">Operator:</span>
                    <span className="tooltip-value">{hoverInfo.feature.properties.company_name || '—'}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Dynamic WMS layers from registry */}
          {Object.entries(activeWmsLayers).map(([layerId, visible]) => {
            if (!visible) return null;
            const allGroups = [
              ...LAYER_GROUPS,
              ...FINLAND_LAYER_GROUPS,
              ...ITALY_LAYER_GROUPS,
              ...DATACENTER_LAYER_GROUPS,
            ];
            // Find the group + layer, then filter by selectedCountry
            let layerDef = null;
            let groupCountry = null;
            for (const g of allGroups) {
              const found = g.layers.find(l => l.id === layerId);
              if (found) { layerDef = found; groupCountry = g.country; break; }
            }
            if (!layerDef) return null;
            // Geojson-kind entries (e.g. existing data centers) toggle on but
            // aren't rendered through this raster loop. Skip rather than build
            // a broken WMS URL with undefined fields.
            if (layerDef.type !== 'wms' && layerDef.type !== 'tiles') return null;
            const layerCountry = layerDef.country ?? groupCountry;
            if (layerCountry && layerCountry !== 'ALL' && layerCountry !== selectedCountry) return null;
            let tileUrl;
            if (layerDef.type === 'tiles') {
              tileUrl = layerDef.url;
            } else {
              const ver = layerDef.wmsVersion ?? '1.3.0';
              const crsParam = ver === '1.1.1' ? 'SRS' : 'CRS';
              tileUrl = `${layerDef.url}?SERVICE=WMS&VERSION=${ver}&REQUEST=GetMap&LAYERS=${layerDef.wmsLayer}&STYLES=&BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256&${crsParam}=EPSG:3857&FORMAT=image/png&TRANSPARENT=true&EXCEPTIONS=BLANK`;
            }
            return (
              <Source key={layerId} id={`wms-reg-${layerId}`} type="raster" tiles={[tileUrl]} tileSize={256}>
                <Layer
                  id={`wms-reg-layer-${layerId}`}
                  type="raster"
                  minzoom={layerDef.minzoom ?? 0}
                  paint={{ 'raster-opacity': layerDef.opacity ?? 0.7 }}
                />
              </Source>
            );
          })}

          {/* Feature 1C: Slope grid overlay */}
          {slopeGrid && slopeGrid.length > 0 && (() => {
            const goodPts = slopeGrid.filter(p => p.slope_pct != null && p.slope_pct <= slopeThreshold);
            const badPts  = slopeGrid.filter(p => p.slope_pct != null && p.slope_pct > slopeThreshold);
            const toFC = pts => ({
              type: 'FeatureCollection',
              features: pts.map(p => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [p.lon, p.lat] }, properties: { slope_pct: p.slope_pct } }))
            });
            return (
              <>
                <Source id="slope-grid-good" type="geojson" data={toFC(goodPts)}>
                  <Layer id="slope-grid-good-circles" type="circle"
                    paint={{ 'circle-radius': 6, 'circle-color': '#22c55e', 'circle-opacity': 0.8, 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' }} />
                </Source>
                <Source id="slope-grid-bad" type="geojson" data={toFC(badPts)}>
                  <Layer id="slope-grid-bad-circles" type="circle"
                    paint={{ 'circle-radius': 6, 'circle-color': '#ef4444', 'circle-opacity': 0.8, 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' }} />
                </Source>
              </>
            );
          })()}

          {/* Feature 1C: Suggested flat-area polygon from terrain-clip */}
          {suggestedPolygon && (
            <Source id="suggested-polygon-source" type="geojson" data={{ type: 'Feature', geometry: suggestedPolygon, properties: {} }}>
              <Layer id="suggested-polygon-fill" type="fill"
                paint={{ 'fill-color': '#22c55e', 'fill-opacity': 0.15 }} />
              <Layer id="suggested-polygon-outline" type="line"
                paint={{ 'line-color': '#22c55e', 'line-width': 2, 'line-dasharray': [4, 2] }} />
            </Source>
          )}

          {/* LCOH Heatmap Layer */}
          {lcohHeatmapVisible && lcohGeoJSON.features.length > 0 && (
            <Source id="lcoh-heatmap-source" type="geojson" data={lcohGeoJSON}>
              <Layer
                id="lcoh-heatmap"
                type="heatmap"
                paint={{
                  'heatmap-weight': ['interpolate', ['linear'], ['get', 'lcoh'],
                    1.0, 1.0,
                    6.0, 0.1,
                  ],
                  'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
                    0, 'rgba(0,0,0,0)',
                    0.2, '#22c55e',
                    0.5, '#eab308',
                    0.8, '#f97316',
                    1.0, '#ef4444',
                  ],
                  'heatmap-radius': 40,
                  'heatmap-opacity': 0.65,
                }}
              />
            </Source>
          )}

        </Map>

        {/* CoordinateSearchBox moved inside SiteFinder (under the Find
            Sites button) per user request 2026-05-27 — was crowding
            the bottom-centre of the map. Still gets the same
            lookupParcelAtCoordinates handler, just plumbed through
            SiteFinder's onCoordLookup prop. */}

        {/* Free-tier banner — top-center, per-session dismissible. */}
        {!isPremium && !bannerDismissed && (
          <div className="free-tier-banner">
            <span className="free-tier-banner-text">
              <strong>Free tier:</strong> {regionLock.label || 'Stavanger'} region only.
            </span>
            <button
              type="button"
              className="free-tier-banner-cta"
              onClick={() => openCalendly(auth0User)}
            >
              Book a call for full Norway →
            </button>
            <button
              type="button"
              className="free-tier-banner-feedback"
              title="Send feedback"
              onClick={openFeedback}
            >
              💬 Feedback
            </button>
            <button
              className="free-tier-banner-close"
              onClick={() => {
                setBannerDismissed(true);
                try { sessionStorage.setItem('seidr.freeTierBannerDismissed', '1'); } catch {}
              }}
              aria-label="Dismiss"
            >×</button>
          </div>
        )}

        {/* When the banner is dismissed (or user is premium), keep a small
            feedback pill in the same top-center spot so it stays reachable. */}
        {(isPremium || bannerDismissed) && (
          <button
            type="button"
            className="feedback-pill-standalone"
            title="Send feedback"
            onClick={openFeedback}
          >
            💬 Feedback
          </button>
        )}

        {/* LCOH heatmap legend */}
        {lcohHeatmapVisible && (
          <div className="lcoh-map-legend">
            <div className="lcoh-map-legend-title">{t('siteScreener.lcohLegend')}</div>
            <div className="lcoh-map-legend-scale">
              <div className="lcoh-map-legend-gradient" />
              <div className="lcoh-map-legend-labels">
                <span>$1/kg</span><span>$3/kg</span><span>$5+</span>
              </div>
            </div>
            <div className="lcoh-map-legend-disclaimer">{t('siteScreener.lcohDisclaimer')}</div>
          </div>
        )}

        {/* SPEC_DEMO.md PR-4: disableLayerControls hides the layer/LCOH/
            draw/SiteFinder UI overlays. The Drawn Area Analysis Panel
            below is implicitly gated too — it only renders when
            drawnArea is set, which can only happen via the Draw button. */}
        {!disableLayerControls && (
          <>
            {/* Layer Toggle Panel */}
            <LayerControls
              layers={layers}
              onToggleLayer={handleToggleLayer}
              currentStyle={mapStyle}
              onStyleChange={setMapStyle}
              onWmsLayerChange={handleWmsLayerChange}
              language={language}
              selectedCountry={selectedCountry}
              useCase={useCase}
            />

            {/* LCOH Heatmap Toggle Button (premium-only) */}
            {searchResultSites.length > 0 && (
              isPremium ? (
                <button
                  className={`map-tool-btn ${lcohHeatmapVisible ? 'active' : ''}`}
                  title={t('siteScreener.showLcohMap')}
                  onClick={() => setLcohHeatmapVisible(v => !v)}
                >
                  LCOH Map
                </button>
              ) : (
                <PremiumLock category="LCOH Map" variant="lock">
                  <button
                    className="map-tool-btn"
                    title="LCOH Map — available in Premium"
                    type="button"
                  >
                    LCOH Map
                  </button>
                </PremiumLock>
              )
            )}

            {/* Draw Tool Activate Button (premium-only) */}
            {isPremium ? (
              <button
                className={`draw-tool-btn ${drawActive ? 'active' : ''}`}
                style={analysisResult ? { left: '440px' } : undefined}
                title={drawActive ? 'Click to place points, double-click to finish' : 'Draw a polygon to analyse terrain and hazards'}
                onClick={() => {
                  if (!drawRef.current) return;
                  if (drawActive) {
                    drawRef.current.changeMode('simple_select');
                    setDrawActive(false);
                  } else {
                    drawRef.current.deleteAll();
                    setDrawnArea(null);
                    drawRef.current.changeMode('draw_polygon');
                    setDrawActive(true);
                  }
                }}
              >
                <span className="draw-btn-icon">{drawActive ? '✕' : '⬡'}</span>
                <span className="draw-btn-label">{drawActive ? 'Cancel' : 'Draw Area'}</span>
              </button>
            ) : (
              <PremiumLock category="Draw Area" variant="lock">
                <button
                  className="draw-tool-btn"
                  style={analysisResult ? { left: '440px' } : undefined}
                  title="Custom polygon screening — available in Premium"
                  type="button"
                >
                  <span className="draw-btn-icon">⬡</span>
                  <span className="draw-btn-label">Draw Area</span>
                </button>
              </PremiumLock>
            )}
          </>
        )}

        {/* Drawn Area Analysis Panel */}
        {drawnArea && (
          <div className="draw-area-panel" style={analysisResult ? { left: '440px' } : undefined}>
            <div className="dap-header">
              <span className="dap-title">Area Analysis</span>
              <button className="dap-clear" onClick={() => {
                drawRef.current?.deleteAll();
                setDrawnArea(null);
              }}>✕</button>
            </div>

            {/* Size metrics */}
            <div className="dap-metrics">
              <div className="dap-metric">
                <div className="dap-metric-value">{drawnArea.ha} ha</div>
                <div className="dap-metric-label">Area</div>
              </div>
              <div className="dap-metric">
                <div className="dap-metric-value">{drawnArea.m2.toLocaleString()} m²</div>
                <div className="dap-metric-label">m²</div>
              </div>
              <div className="dap-metric">
                <div className="dap-metric-value">{drawnArea.perimeterKm} km</div>
                <div className="dap-metric-label">Perimeter</div>
              </div>
            </div>

            {/* Terrain + hazard enrichment */}
            {drawnArea.loading ? (
              <div className="dap-loading">Fetching terrain data…</div>
            ) : (
              <div className="dap-facts">
                {drawnArea.elevation_m != null && (
                  <div className="dap-fact">
                    <span className="dap-fact-label">Elevation</span>
                    <span className="dap-fact-value">{drawnArea.elevation_m} m</span>
                  </div>
                )}
                {drawnArea.slope_pct != null && (
                  <div className={`dap-fact ${drawnArea.slope_pct > 15 ? 'dap-fact-warn' : drawnArea.slope_pct > 8 ? 'dap-fact-amber' : 'dap-fact-ok'}`}>
                    <span className="dap-fact-label">Slope</span>
                    <span className="dap-fact-value">{drawnArea.slope_pct}%
                      <span className="dap-fact-sub">{drawnArea.slope_pct > 15 ? ' · Too steep' : drawnArea.slope_pct > 8 ? ' · Marginal' : ' · OK'}</span>
                    </span>
                  </div>
                )}
                <div className={`dap-fact ${drawnArea.flood_1000yr ? 'dap-fact-warn' : 'dap-fact-ok'}`}>
                  <span className="dap-fact-icon">{drawnArea.flood_1000yr ? '⚠' : '✓'}</span>
                  <span className="dap-fact-label">Flood zone</span>
                  <span className="dap-fact-value">{drawnArea.flood_1000yr === null ? '—' : drawnArea.flood_1000yr ? '1000-yr zone' : 'Clear'}</span>
                </div>
                <div className={`dap-fact ${drawnArea.landslide ? 'dap-fact-warn' : 'dap-fact-ok'}`}>
                  <span className="dap-fact-icon">{drawnArea.landslide ? '⚠' : '✓'}</span>
                  <span className="dap-fact-label">Landslide</span>
                  <span className="dap-fact-value">{drawnArea.landslide === null ? '—' : drawnArea.landslide ? 'Hazard zone' : 'Clear'}</span>
                </div>
                {drawnArea.quick_clay != null && (
                  <div className={`dap-fact ${drawnArea.quick_clay ? 'dap-fact-warn' : 'dap-fact-ok'}`}>
                    <span className="dap-fact-icon">{drawnArea.quick_clay ? '⚠' : '✓'}</span>
                    <span className="dap-fact-label">Quick clay</span>
                    <span className="dap-fact-value">{drawnArea.quick_clay ? 'Zone present' : 'Clear'}</span>
                  </div>
                )}
              </div>
            )}

            <div className="dap-hint">Centroid check · {drawnArea.centroid?.lat.toFixed(4)}°N {drawnArea.centroid?.lon.toFixed(4)}°E</div>
          </div>
        )}

        {/* Site Finder Panel with toggle tab. Gated by
            disableLayerControls (PR-4) — the demo replaces this UI
            with its own DemoControls panel rendered outside the map. */}
        {!disableLayerControls && (
          <SiteFinder
            isOpen={showSiteFinder}
            onToggle={() => setShowSiteFinder(!showSiteFinder)}
            onClose={() => setShowSiteFinder(false)}
            onSiteSelect={handleSiteSelect}
            onSubstationPan={handleSubstationPan}
            onSearchStart={handleSearchStart}
            mapBounds={mapBounds}
            onCriteriaChange={setTeigCriteria}
            onResultsChange={setSearchResultSites}
            onTeigSelect={handleSearchTeigSelect}
            onTeigResultsChange={setSearchTeigs}
            onCountryChange={setSelectedCountry}
            onUseCaseChange={setUseCase}
            onCoordLookup={lookupParcelAtCoordinates}
            language={language}
          />
        )}

        {/* Zoom-in prompt — shown when a substation is selected but zoom is too low */}
        {showZoomPrompt && (
          <div className="nearby-parcels-loading" style={{ color: '#f59e0b' }}>
            {t('siteScreener.zoomPrompt')}
          </div>
        )}

        {/* Teiger viewport toast */}
        {(nearbyParcelsLoading || nearbyParcelsEnriching || nearbyParcelCount !== null) && (
          <div className="nearby-parcels-loading">
            {nearbyParcelsLoading
              ? 'Loading parcels…'
              : nearbyParcelsEnriching
                ? `Checking zones for ${nearbyParcelCount} parcels…`
                : nearbyParcelCount === 0
                  ? 'No parcels in this area'
                  : `${nearbyParcelCount} parcels — green = suitable zone + grid OK`}
          </div>
        )}

        {/* Green Teiger List — shown when enriched teiger are available */}
        {nearbyParcels?.features?.length > 0 && (() => {
          // isGreen: teig-level evaluation — all criteria measured from teig centroid.
          // Phase 1 (substation filtering) uses only min_voltage in SQL.
          // Everything here is Phase 2 teig-level classification.
          const isGreen = f => {
            const p = f.properties || {};
            // Hard stop flags
            if (p.har_kulturminne === 'true' || p.tvist === 'true' || p.har_forurensning === 'true') return false;
            if (p.flood_zone === true || p.landslide_zone === true) return false;
            if (p.kvikkleire_hard_stop === true) return false;
            if (p.salmon_river_within_500m === true) return false;
            if (p.nin_type_a_conflict === true) return false;
            // Zone suitability (teig-level)
            const suit = p.zone_suitability != null ? parseFloat(p.zone_suitability) : null;
            if (suit === null || suit < 0.6) return false;
            // Distance from teig centroid to nearest substation (teig-level)
            if (p.nearest_grid_distance_km != null && p.nearest_grid_distance_km > teigCriteria.maxGridDistance) return false;
            if (p.nearest_grid_voltage_kv != null && p.nearest_grid_voltage_kv < teigCriteria.minVoltage) return false;
            // Distance from teig centroid to nearest protected area (teig-level)
            if (p.nearest_protected_distance_km != null && p.nearest_protected_distance_km < teigCriteria.minProtectedDistance) return false;
            // Distance from teig centroid to nearest port (teig-level)
            if (p.nearest_port_distance_km != null && teigCriteria.maxPortDistance != null && p.nearest_port_distance_km > teigCriteria.maxPortDistance) return false;
            // Parcel area
            const areaHa = p.areal_ha != null ? parseFloat(p.areal_ha) : null;
            if (areaHa !== null && areaHa < teigCriteria.minAreaHa) return false;
            return true;
          };
          const greenTeiger = nearbyParcels.features
            .filter(isGreen)
            .sort((a, b) => parseFloat(b.properties.zone_suitability) - parseFloat(a.properties.zone_suitability));
          if (!greenTeiger.length) return null;
          return (
            <div className="teig-list-panel">
              <button className="teig-list-header" onClick={() => setTeigListOpen(o => !o)}>
                <span className="teig-list-dot" />
                <span>{greenTeiger.length} green parcels</span>
                <span className="teig-list-chevron">{teigListOpen ? '▼' : '▲'}</span>
              </button>
              {teigListOpen && (
                <div className="teig-list-items">
                  {greenTeiger.map((f, i) => {
                    const p = f.properties || {};
                    const area = (turf.area(f) / 10000).toFixed(1);
                    const suitPct = Math.round(parseFloat(p.zone_suitability) * 100);
                    const isSelected = selectedTeig === f;
                    return (
                      <div
                        key={p.teigid || p.matrikkelnummer || i}
                        className={`teig-list-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => selectNearbyTeig(f)}
                      >
                        <div className="tli-left">
                          <div className="tli-id">{p.matrikkelnummer || p.teigid || '–'}</div>
                          <div className="tli-zone">{p.zone_name || `PBL ${p.zone_code}`}</div>
                        </div>
                        <div className="tli-right">
                          <div className="tli-suit">{suitPct}%</div>
                          <div className="tli-area">{area} ha</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* Parcel selection mode overlay — only shows area counter + controls */}
        {parcelSelectMode && (
          <div className="parcel-selection-panel" style={analysisResult ? { left: 430 } : {}}>
            <div className="parcel-panel-title">Velg tomter</div>
            <div className="parcel-area-row">
              <span>Valgt areal</span>
              <span className={totalSelectedHa >= (neededAreaHa || Infinity) ? 'area-met' : 'area-short'}>
                {totalSelectedHa < 0.1 && totalSelectedHa > 0 ? totalSelectedHa.toFixed(3) : totalSelectedHa.toFixed(2)} ha
              </span>
            </div>
            {neededAreaHa && (
              <>
                <div className="parcel-area-row muted">
                  <span>Nødvendig</span>
                  <span>~{neededAreaHa.toFixed(1)} ha</span>
                </div>
                <div className="parcel-progress-track">
                  <div className="parcel-progress-fill" style={{
                    width: `${Math.min(100, (totalSelectedHa / neededAreaHa) * 100)}%`,
                    background: totalSelectedHa >= neededAreaHa ? '#10b981' : '#22d3ee'
                  }} />
                </div>
              </>
            )}
            <div className="parcel-actions">
              <button className="parcel-clear-btn" onClick={() => setSelectedParcels([])}>Nullstill</button>
              <button className="parcel-done-btn" onClick={() => { setParcelMode(false); setNearbyParcels(null); }}>Ferdig</button>
            </div>
          </div>
        )}

        {/* Analysis Panel. `key` forces a full remount when the user
            clicks a different site so all on-demand fetch state
            (gridFeasData, gridQueueData, gridAiData, costData,
            konsesjonData) resets cleanly. Without the key, React
            reuses the same instance and stale results from the
            previous site's "Check grid feasibility" / cost recalc
            persist until manually cleared.
            Audit 2026-05-28 — user reported "Are we sure clicking on
            sites gives us new coordinates in the site analysis?" */}
        {analysisResult && (
          <AnalysisPanel
            key={`${analysisResult.location?.latitude ?? 'na'}-${analysisResult.location?.longitude ?? 'na'}`}
            analysis={analysisResult}
            onLocate={handleLocate}
            onClose={() => { setAnalysisResult(null); setBuildablePolygon(null); }}
            onAreaChange={(ha) => setNeededAreaHa(ha)}
            projectMw={projectMw}
            onProjectMwChange={(mw) => {
              if (!Number.isFinite(mw) || mw <= 0) return;
              projectMwUserSetRef.current = true;
              setDefaultMwNote(false);
              setProjectMw(mw);
            }}
            enriching={teigEnriching}
            defaultMwNote={defaultMwNote}
            selectedCountry={selectedCountry}
            useCase={useCase}
            onBuildablePolygonChange={setBuildablePolygon}
          />
        )}
      </div>

    </div>
  );
};

export default SiteScreener;
